import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { pushAdminSyncToApi, pushAdminSyncToApiDeferred } from '../lib/adminSyncApi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { EstabelecimentoDescricaoEditor } from '../components/EstabelecimentoDescricaoEditor';
import { StatusProgramadoAgendaPanel } from '../components/StatusProgramadoAgendaPanel';
import { useAdminCatalog, useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { useAdminUnsaved } from '../contexts/AdminUnsavedContext';
import { useAdminDialog } from '../contexts/AdminDialogContext';
import {
  addEstabelecimento,
  getEstabelecimentos,
  getEstabelecimentoById,
  mergeEstabelecimentoSubcategoryIds,
  saveEstabelecimento,
  removeEstabelecimento,
  horarioPadrao,
  DIAS_SEMANA,
  formatarHorarioDia,
  type Estabelecimento,
  type EstabelecimentoLayoutType,
  type DescontoMoradorModo,
  type EstabelecimentoResponsavel,
  type HorarioSemanal,
  type HorarioDia,
  type DiaSemana,
  type ExcecaoDia,
} from '../lib/estabelecimentosStorage';
import {
  getComercioRestauranteUsers,
} from '../lib/newsStorage';
import {
  getEstabelecimentoCategories,
  getEstabelecimentoSubcategories,
  getEstabelecimentoSubcategoriesByCategory,
  addEstabelecimentoCategory,
  addEstabelecimentoSubcategory,
  removeEstabelecimentoCategory,
  removeEstabelecimentoSubcategory,
} from '../lib/estabelecimentosCategoriasStorage';
import { getComercioProfile } from '../lib/comercioProfilesStorage';
import { getEventsByEstabelecimento } from '../lib/eventsStorage';
import { addEstabelecimentoTag, getEstabelecimentoTags } from '../lib/estabelecimentosTagsStorage';
import { getCidades, type Cidade } from '../lib/experienciasStorage';
import { apiUrl, getApiBase } from '../lib/api';
import { adminApiHeaders } from '../lib/adminApiAuth';
import { isAdminApiIngestConfigured } from '../lib/adminIngestApi';
import { parseGoogleMapsLink, looksLikeMapsLink } from '../lib/parseMapsLink';
import { fetchPlacePhotos, extractPlaceIdFromUrl } from '../lib/fetchPlacePhotos';
import { compressImageFileForStorage, processImageAutoAspect } from '../lib/imageProcessing';
import { uploadAdminMedia } from '../lib/mediaUploadApi';
import { resolveMediaUrl } from '../../../shared/lib/mediaUrl';
import { htmlToPlainText } from '../../../shared/lib/richHtmlUtils';
import { ImageWithMediaFallback } from '../components/ImageWithMediaFallback';
import { TimeSelect } from '../components/TimeSelect';
import {
  hojeYmdLocal,
  MSG_AVISO_PERIODOS_SOBREPOSTOS,
  periodosStatusTemSobreposicao,
  ymdReferenciaAgendaStatusLocal,
} from '../lib/statusProgramadoUtils';

type EstHistoricoStatus = NonNullable<Estabelecimento['statusProgramadoHistorico']>[number];

const MAX_FOTOS_ESTABELECIMENTO = 8;

function toDateInputValue(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isoFromDateAndTimeLocal(dateStr: string, timeStr: string): string {
  const date = dateStr.trim();
  if (!date) return '';
  const raw = (timeStr?.trim() || '23:59').slice(0, 5);
  const [hh = '0', mm = '0'] = raw.split(':');
  const h = Math.min(23, Math.max(0, parseInt(hh, 10) || 0));
  const m = Math.min(59, Math.max(0, parseInt(mm, 10) || 0));
  const pad = (n: number) => String(n).padStart(2, '0');
  const combined = `${date}T${pad(h)}:${pad(m)}:00`;
  const local = new Date(combined);
  if (Number.isNaN(local.getTime())) return '';
  return local.toISOString();
}

function mergeValidityDateWithSaveTimeNow(dateYmd: string): string {
  const date = dateYmd.trim();
  if (!date) return '';
  const parts = date.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '';
  const [y, mo, d] = parts;
  const now = new Date();
  const local = new Date(y, mo - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  if (Number.isNaN(local.getTime())) return '';
  return local.toISOString();
}

/** Combobox de status + destaques (mesma regra que notícias; rótulos no masculino para estabelecimento). */
type ModoEstabelecimentoOpcao = 'ativado' | 'principal' | 'naviex' | 'categoria' | 'desativar';

const EST_MODO_OPTIONS: { id: ModoEstabelecimentoOpcao; label: string }[] = [
  { id: 'ativado', label: 'Ativado' },
  { id: 'naviex', label: 'Destaque Naviex' },
  { id: 'principal', label: 'Destaque Principal' },
  { id: 'categoria', label: 'Destaque Categoria' },
  { id: 'desativar', label: 'Desativado' },
];

type ListaEstStatusDropdownState = { estabelecimentoId: string; top: number; left: number };

const EST_MODO_LISTA_DROPDOWN_CLASS =
  'fixed z-[200] w-max rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden px-2 py-1';

type EstabelecimentoFormStatusSlice = {
  statusPadrao: 'ativado' | 'desativado';
  layoutType: EstabelecimentoLayoutType;
  destaqueSubcategoria: boolean;
  destaqueNaviex: boolean;
};

function getModoEstabelecimentoFromForm(f: EstabelecimentoFormStatusSlice): ModoEstabelecimentoOpcao {
  if (f.statusPadrao === 'desativado') return 'desativar';
  if (f.destaqueNaviex) return 'naviex';
  if (f.layoutType === 'matéria_principal' || f.layoutType === 'slide_principal') return 'principal';
  if (f.destaqueSubcategoria) return 'categoria';
  return 'ativado';
}

function getModoEstabelecimentoFromItem(est: Estabelecimento): ModoEstabelecimentoOpcao {
  if ((est.statusPadrao ?? 'ativado') === 'desativado') return 'desativar';
  if (est.destaqueNaviex) return 'naviex';
  if (est.layoutType === 'matéria_principal' || est.layoutType === 'slide_principal') return 'principal';
  if (est.destaqueSubcategoria) return 'categoria';
  return 'ativado';
}

function buildUpdateModoEstabelecimento(m: ModoEstabelecimentoOpcao): Partial<{
  statusPadrao: 'ativado' | 'desativado';
  layoutType: EstabelecimentoLayoutType;
  layoutExpiresAt: string;
  destaqueSubcategoria: boolean;
  destaqueSubcategoriaExpiresAt: string;
  destaqueNaviex: boolean;
  destaqueNaviexExpiresAt: string;
}> {
  switch (m) {
    case 'ativado':
      return {
        statusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'desativar':
      return {
        statusPadrao: 'desativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'principal':
      return {
        statusPadrao: 'ativado',
        layoutType: 'matéria_principal',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'categoria':
      return {
        statusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: true,
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'naviex':
      return {
        statusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: true,
      };
  }
}

function patchEstabelecimentoForModoStatus(est: Estabelecimento, modo: ModoEstabelecimentoOpcao): Estabelecimento {
  const patch = buildUpdateModoEstabelecimento(modo);
  const next: Estabelecimento = { ...est };
  delete next.periodosStatus;

  if (patch.statusPadrao !== undefined) next.statusPadrao = patch.statusPadrao;
  if (patch.layoutType !== undefined) next.layoutType = patch.layoutType;

  if (patch.layoutExpiresAt === '') next.layoutExpiresAt = undefined;

  if (patch.destaqueSubcategoria === false) {
    next.destaqueSubcategoria = undefined;
    next.destaqueSubcategoriaExpiresAt = undefined;
  } else if (patch.destaqueSubcategoria === true) {
    next.destaqueSubcategoria = true;
  }

  if (patch.destaqueSubcategoriaExpiresAt === '') next.destaqueSubcategoriaExpiresAt = undefined;

  if (patch.destaqueNaviex === false) {
    next.destaqueNaviex = undefined;
    next.destaqueNaviexExpiresAt = undefined;
  } else if (patch.destaqueNaviex === true) {
    next.destaqueNaviex = true;
  }

  if (patch.destaqueNaviexExpiresAt === '') next.destaqueNaviexExpiresAt = undefined;

  return next;
}

function statusModoEstButtonClass(m: ModoEstabelecimentoOpcao): string {
  const base =
    'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all min-w-[11rem] max-w-[16rem] justify-between';
  switch (m) {
    case 'desativar':
      return `${base} bg-red-500/15 border-red-500 text-red-800`;
    case 'ativado':
      return `${base} bg-emerald-500/15 border-emerald-500 text-emerald-800`;
    default:
      return `${base} bg-amber-500/12 border-amber-500 text-amber-900`;
  }
}

/** Combobox compacto na coluna Status da listagem. */
function statusModoEstListaButtonClass(m: ModoEstabelecimentoOpcao): string {
  const base =
    'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border-2 max-w-[min(14rem,100%)] min-w-[9.5rem] justify-between transition-all hover:brightness-95';
  switch (m) {
    case 'desativar':
      return `${base} bg-red-500/15 border-red-500 text-red-800`;
    case 'ativado':
      return `${base} bg-emerald-500/15 border-emerald-500 text-emerald-800`;
    default:
      return `${base} bg-amber-500/12 border-amber-500 text-amber-900`;
  }
}

function statusModoEstDotClass(m: ModoEstabelecimentoOpcao): string {
  switch (m) {
    case 'desativar':
      return 'bg-red-500';
    case 'ativado':
      return 'bg-emerald-500';
    case 'principal':
      return 'bg-amber-500';
    case 'naviex':
      return 'bg-violet-500';
    case 'categoria':
      return 'bg-sky-600';
  }
}

type GoogleAddressComponent = { long_name: string; types: string[] };

function normalizarNomeCidadeEstabelecimento(s: string): string {
  return (s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Nomes candidatos a município (ordem favorece admin_level_2 no Brasil, onde locality pode ser bairro). */
function cidadesCandidatasDeComponentesGoogle(comps: GoogleAddressComponent[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (s?: string) => {
    const t = s?.trim();
    if (!t) return;
    const key = normalizarNomeCidadeEstabelecimento(t);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(t);
  };
  const adm2 = comps.find((c) => c.types.includes('administrative_area_level_2'));
  add(adm2?.long_name);
  const locality = comps.find((c) => c.types.includes('locality'));
  add(locality?.long_name);
  const admin3 = comps.find((c) => c.types.includes('administrative_area_level_3'));
  add(admin3?.long_name);
  const subloc = comps.find((c) => c.types.includes('sublocality') && c.types.includes('political'));
  add(subloc?.long_name);
  return out;
}

/** Só aceita cidade que exista em `getCidades()` (nome compatível). */
function encontrarCidadeCadastradaPorNome(
  nomeGoogle: string | undefined,
  todasCidades: Cidade[]
): Cidade | null {
  if (!nomeGoogle?.trim() || todasCidades.length === 0) return null;
  const gNorm = normalizarNomeCidadeEstabelecimento(nomeGoogle);
  return (
    todasCidades.find((c) => {
      const cNorm = normalizarNomeCidadeEstabelecimento(c.cidade);
      return cNorm === gNorm || cNorm.includes(gNorm) || gNorm.includes(cNorm);
    }) ?? null
  );
}

function matchCidadePorComponentesGoogle(
  comps: GoogleAddressComponent[] | undefined,
  todasCidades: Cidade[]
): Cidade | null {
  if (!comps?.length || todasCidades.length === 0) return null;
  for (const nome of cidadesCandidatasDeComponentesGoogle(comps)) {
    const m = encontrarCidadeCadastradaPorNome(nome, todasCidades);
    if (m) return m;
  }
  return null;
}

/** Nome do estabelecimento a partir do Place (após escolher sugestão). */
function nomeEstabelecimentoDoGooglePlace(place: { name?: string; formatted_address?: string }): string {
  const n = place.name?.trim();
  if (n) return n;
  return place.formatted_address?.split(',')[0]?.trim() ?? '';
}

function reverseGeocodeMatchCidadeCadastrada(
  lat: number,
  lng: number,
  todasCidades?: Cidade[]
): Promise<Cidade | null> {
  const listaCidades = todasCidades ?? getCidades();
  const w = window as {
    google?: {
      maps?: {
        Geocoder: new () => {
          geocode: (req: unknown, cb: (results: unknown, status: string) => void) => void;
        };
      };
    };
  };
  return new Promise((resolve) => {
    if (!w.google?.maps?.Geocoder) {
      resolve(null);
      return;
    }
    const geocoder = new w.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results: unknown, status: string) => {
      if (status !== 'OK' || !Array.isArray(results) || !results[0]) {
        resolve(null);
        return;
      }
      const comps = (results[0] as { address_components?: GoogleAddressComponent[] }).address_components ?? [];
      resolve(matchCidadePorComponentesGoogle(comps, listaCidades));
    });
  });
}

const DIAS_LABEL: Record<DiaSemana, string> = {
  seg: 'Segunda',
  ter: 'Terça',
  qua: 'Quarta',
  qui: 'Quinta',
  sex: 'Sexta',
  sab: 'Sábado',
  dom: 'Domingo',
};

/** Mapeia getDay() (0=domingo) para DiaSemana */
const DIA_SEMANA_FROM_JS: Record<number, DiaSemana> = { 0: 'dom', 1: 'seg', 2: 'ter', 3: 'qua', 4: 'qui', 5: 'sex', 6: 'sab' };

/** Formata CNPJ como XX.XXX.XXX/XXXX-XX. Aceita apenas dígitos. */
function formatCnpj(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/** Valida CNPJ (14 dígitos, dígitos verificadores corretos, rejeita sequências iguais). */
function isValidCnpj(cnpj: string): boolean {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false; // todos iguais
  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let soma = 0;
  for (let i = 0; i < 12; i++) soma += parseInt(digits[i], 10) * pesos1[i];
  let dv1 = soma % 11;
  dv1 = dv1 < 2 ? 0 : 11 - dv1;
  if (parseInt(digits[12], 10) !== dv1) return false;
  soma = 0;
  for (let i = 0; i < 13; i++) soma += parseInt(digits[i], 10) * pesos2[i];
  let dv2 = soma % 11;
  dv2 = dv2 < 2 ? 0 : 11 - dv2;
  if (parseInt(digits[13], 10) !== dv2) return false;
  return true;
}

/** Interpreta valor numérico de % ou R$ (aceita vírgula decimal e separador de milhares). */
function parseValorDescontoMoradorNumero(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '');
  if (!t) return null;
  const normalized = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Campos obrigatórios para publicar (espelha `buildPayloadOrError`, com mensagens curtas para alertas). */
function buildEstabelecimentoPublicacaoIssues(
  f: {
    nome: string;
    resumo: string;
    descricao: string;
    cnpj: string;
    cityId: string;
    categoryId: string;
    subcategoryIds: string[];
    endereco: string;
    lat?: number;
    lng?: number;
    source?: 'manual' | 'google_import' | undefined;
    descontoMoradorAtivo?: boolean;
    descontoMoradorModo?: DescontoMoradorModo;
    descontoMoradorValor?: string;
    descontoMoradorTexto?: string;
  },
  googleImport: boolean,
  cidadesList: Cidade[]
): string[] {
  const issues: string[] = [];
  if (!f.nome.trim()) issues.push('Nome');
  if (!f.resumo.trim()) issues.push('Resumo');
  if (!htmlToPlainText(f.descricao)) issues.push('Descrição');
  const cnpjDigits = f.cnpj.trim().replace(/\D/g, '');
  if (!googleImport) {
    if (cnpjDigits.length === 0) issues.push('CNPJ');
    else if (cnpjDigits.length !== 14) issues.push('CNPJ com 14 dígitos');
    else if (!isValidCnpj(f.cnpj)) issues.push('CNPJ válido');
  }
  if (!f.cityId) issues.push('Cidade');
  else if (!cidadesList.some((c) => c.id === f.cityId)) issues.push('Cidade cadastrada');
  if (!f.categoryId) issues.push('Categoria');
  const subs = f.categoryId ? getEstabelecimentoSubcategoriesByCategory(f.categoryId) : [];
  const validSubs = f.subcategoryIds.filter((id) => subs.some((s) => s.id === id));
  if (subs.length > 0 && validSubs.length === 0) issues.push('Subcategoria');
  const temEnderecoOuCoords = ((f.endereco ?? '').trim().length > 0) || (f.lat != null && f.lng != null);
  if (!temEnderecoOuCoords) issues.push('Localização (mapa ou coordenadas)');
  if (f.descontoMoradorAtivo) {
    const modo = (f.descontoMoradorModo ?? 'percent') as DescontoMoradorModo;
    if (modo === 'text') {
      if (!f.descontoMoradorTexto?.trim()) issues.push('Desconto morador (texto)');
    } else {
      const n = parseValorDescontoMoradorNumero(f.descontoMoradorValor ?? '');
      if (n == null) issues.push('Desconto morador (valor)');
      else if (modo === 'percent' && n > 100) issues.push('Desconto morador (máx. 100%)');
    }
  }
  return issues;
}

function formatDataBR(dataStr: string): string {
  const [y, m, d] = dataStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** Resultado da busca no Google Places (sem salvar no banco). */
interface ImportPlaceResult {
  googlePlaceId?: string;
  nome: string;
  descricao?: string | null;
  formattedAddress?: string | null;
  lat?: number | null;
  lng?: number | null;
  endereco?: string | null;
  rating?: number | null;
  totalRatings?: number | null;
  source?: 'google_import';
}

function DiaEditModal({
  dataStr,
  exc,
  estabelecimentoId,
  onSave,
  onRemove,
  onClose,
  onCriarEvento,
}: {
  dataStr: string;
  exc: ExcecaoDia | null;
  estabelecimentoId?: string;
  onSave: (exc: ExcecaoDia) => void;
  onRemove: () => void;
  onClose: () => void;
  onCriarEvento?: (dataStr: string) => void;
}) {
  const [tipo, setTipo] = useState<'fechado' | 'horario'>(exc?.tipo === 'horario' ? 'horario' : 'fechado');
  const [abertura, setAbertura] = useState(exc?.tipo === 'horario' ? (exc.abertura ?? '08:00') : '08:00');
  const [fechamento, setFechamento] = useState(exc?.tipo === 'horario' ? (exc.fechamento ?? '18:00') : '18:00');

  const handleSalvar = () => {
    if (tipo === 'fechado') {
      onSave({ tipo: 'fechado' });
    } else {
      onSave({ tipo: 'horario', abertura, fechamento });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6 my-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Editar dia {formatDataBR(dataStr)}</h3>
        <p className="text-sm text-gray-600 mb-4">Defina se o estabelecimento estará fechado ou com horário diferenciado.</p>
        <div className="space-y-4">
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={tipo === 'fechado'}
                onChange={() => setTipo('fechado')}
                className="rounded-full border-gray-300 text-red-500 focus:ring-red-400"
              />
              <span className="text-sm font-medium">Fechado</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={tipo === 'horario'}
                onChange={() => setTipo('horario')}
                className="rounded-full border-gray-300 text-amber-500 focus:ring-amber-400"
              />
              <span className="text-sm font-medium">Horário diferente</span>
            </label>
          </div>
          {tipo === 'horario' && (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Abertura</label>
                <TimeSelect value={abertura} onChange={setAbertura} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fechamento</label>
                <TimeSelect value={fechamento} onChange={setFechamento} />
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3 mt-6">
          <button
            type="button"
            onClick={handleSalvar}
            className="px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 min-h-[44px]"
          >
            Salvar
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 min-h-[44px]"
          >
            Remover exceção
          </button>
          {onCriarEvento && (
            <button
              type="button"
              onClick={() => { onCriarEvento(dataStr); onClose(); }}
              className="px-4 py-2.5 border border-orange-300 text-orange-600 rounded-lg font-medium hover:bg-orange-50 min-h-[44px]"
            >
              Criar evento
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-gray-600 rounded-lg font-medium hover:bg-gray-100 min-h-[44px]"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

type ViewMode = 'list' | 'form';

/** Caixa lateral estilo meta-box (referência WordPress). */
function InformacoesSidebarMeta({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
        <span className="text-xs font-semibold tracking-wide text-gray-800">{title}</span>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function IconEstListEdit() {
  return (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function IconEstListTrash() {
  return (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

export default function EstabelecimentosPage() {
  const { catalogVersion, reloadCatalog } = useAdminCatalog();
  const adminIngestMisconfigured = !isAdminApiIngestConfigured();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [estabelecimentos, setEstabelecimentos] = useState<Estabelecimento[]>([]);
  const [categories, setCategories] = useState<ReturnType<typeof getEstabelecimentoCategories>>([]);
  const [subcategories, setSubcategories] = useState<ReturnType<typeof getEstabelecimentoSubcategories>>([]);
  const [cidades, setCidades] = useState<ReturnType<typeof getCidades>>([]);
  const [comercioUsers, setComercioUsers] = useState<EstabelecimentoResponsavel[]>([]);
  const [form, setForm] = useState({
    nome: '',
    subtitulo: '',
    resumo: '',
    descricao: '',
    cnpj: '',
    cityId: '',
    endereco: '',
    lat: undefined as number | undefined,
    lng: undefined as number | undefined,
    mapsZoom: 16 as number,
    categoryId: '',
    subcategoryIds: [] as string[],
    responsaveis: [] as EstabelecimentoResponsavel[],
    statusPadrao: 'ativado' as 'ativado' | 'desativado',
    layoutType: 'normal' as EstabelecimentoLayoutType,
    layoutExpiresAt: '',
    destaqueSubcategoria: false,
    destaqueSubcategoriaExpiresAt: '',
    destaqueNaviex: false,
    destaqueNaviexExpiresAt: '',
    periodosStatus: [] as {
      de: string;
      ate: string;
      tipo: 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria';
    }[],
    periodoDe: '',
    periodoAte: '',
    statusProgramadoHistorico: [] as EstHistoricoStatus[],
    horarioSemanal: horarioPadrao(),
    excecoesCalendario: {} as Record<string, ExcecaoDia>,
    source: undefined as 'manual' | 'google_import' | undefined,
    googlePlaceId: undefined as string | undefined,
    rating: undefined as number | undefined,
    totalRatings: undefined as number | undefined,
    formattedAddress: undefined as string | undefined,
    fotos: [] as string[],
    descontoParceriaDescricao: '',
    descontoMoradorAtivo: false,
    descontoMoradorModo: 'percent' as DescontoMoradorModo,
    descontoMoradorValor: '',
    descontoMoradorTexto: '',
    tags: [] as string[],
  });
  const [tagInputEst, setTagInputEst] = useState('');
  const [tagsPickerTickEst, setTagsPickerTickEst] = useState(0);
  const [responsavelQuery, setResponsavelQuery] = useState('');
  const [responsavelOpen, setResponsavelOpen] = useState(false);
  const [formTab, setFormTab] = useState<
    | 'informacoes'
    | 'localizacao'
    | 'imagens'
    | 'descontoParceria'
    | 'status'
    | 'funcionamento'
    | 'calendarioEventos'
  >('informacoes');
  /** Dia modelo para «Aplicar a todos os dias» no horário semanal (igual Experiências). */
  const [diaFonteHorarioEst, setDiaFonteHorarioEst] = useState<DiaSemana>('seg');
  const [importFotosLoading, setImportFotosLoading] = useState(false);
  const [imagemUploadBusy, setImagemUploadBusy] = useState(false);
  const [draggingFotoIndex, setDraggingFotoIndex] = useState<number | null>(null);
  const [dragOverFotoIndex, setDragOverFotoIndex] = useState<number | null>(null);
  const [calAno, setCalAno] = useState(() => new Date().getFullYear());
  const [calMes, setCalMes] = useState(() => new Date().getMonth());
  const [diaEditModal, setDiaEditModal] = useState<{ dataStr: string; exc: ExcecaoDia | null } | null>(null);
  const nomeInputRef = useRef<HTMLInputElement | null>(null);
  const enderecoInputRef = useRef<HTMLInputElement | null>(null);
  const estabelecimentoNomeAutocompleteRef = useRef<{ addListener: (ev: string, fn: () => void) => void } | null>(null);
  const estabelecimentoPlacesAutocompleteRef = useRef<{ addListener: (ev: string, fn: () => void) => void } | null>(null);
  const statusComboRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const mapRef = useRef<{ setCenter: (coords: { lat: number; lng: number }) => void; setZoom: (zoom: number) => void } | null>(null);
  const markerRef = useRef<{ setPosition: (coords: { lat: number; lng: number }) => void } | null>(null);
  const [statusComboContext, setStatusComboContext] = useState<'header' | null>(null);
  /** Combobox de status aberto na coluna da listagem (portal fixo). */
  const [listaStatusDropdown, setListaStatusDropdown] = useState<ListaEstStatusDropdownState | null>(null);
  const listaStatusDropdownRef = useRef<ListaEstStatusDropdownState | null>(null);
  listaStatusDropdownRef.current = listaStatusDropdown;
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [subcategoryModalOpen, setSubcategoryModalOpen] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [newSubcategoryCategoryId, setNewSubcategoryCategoryId] = useState('');
  const [categoriasManagerOpen, setCategoriasManagerOpen] = useState(false);
  const [reassignModal, setReassignModal] = useState<{
    type: 'category' | 'subcategory';
    id: string;
    name: string;
    establishmentsCount: number;
  } | null>(null);
  const [reassignNewCategoryId, setReassignNewCategoryId] = useState('');
  const [reassignNewSubcategoryId, setReassignNewSubcategoryId] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importCityId, setImportCityId] = useState('');
  const [importType, setImportType] = useState<string>('restaurant');
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState<ImportPlaceResult[]>([]);
  const [importSelected, setImportSelected] = useState<Set<string>>(new Set());
  const [rascunhoLeaveModalOpen, setRascunhoLeaveModalOpen] = useState(false);
  /** Filtros da listagem (aba Restaurantes e Comércios). */
  const [listaFilterStatus, setListaFilterStatus] = useState<'' | ModoEstabelecimentoOpcao>('');
  const [listaFilterNome, setListaFilterNome] = useState('');
  const [listaFilterCategoryId, setListaFilterCategoryId] = useState('');
  const [listaFilterCityId, setListaFilterCityId] = useState('');
  const [listaFilterPublicacao, setListaFilterPublicacao] = useState<'' | 'rascunho' | 'publicado' | 'lixeira'>('');
  const isFirstFormRenderRef = useRef(true);
  const persistEstDraftRef = useRef<(opts?: { keepOnForm?: boolean }) => boolean>(() => false);
  const saveEstPublishedRef = useRef<() => Promise<boolean>>(async () => false);
  const autoDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hojePeriodo = hojeYmdLocal();
  const hojeCalendarioSelecao = hojeYmdLocal();
  const [agendaClockTickEst, setAgendaClockTickEst] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setAgendaClockTickEst((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const diaAgendaStatusEst = useMemo(() => ymdReferenciaAgendaStatusLocal(new Date()), [agendaClockTickEst]);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isDirty,
    setDirty,
    setIsPublishedLeave,
    setSkipAutoFlushOnLeave,
    setFlushDraftHandler,
    setSavePublishedHandler,
    setSaveDraftHandler,
  } = useAdminUnsaved();
  const { showAlert, showConfirm } = useAdminDialog();

  /** Igual Experiências: após gravar no localStorage, envia snapshot ao Dynamo (produção / catálogo cliente). */
  const schedulePushAdminSyncAfterMutation = useCallback(() => {
    pushAdminSyncToApiDeferred(async (syncResult) => {
      if (!syncResult.ok) {
        await showAlert(
          `Alterações guardadas localmente, mas a sincronização com a API falhou: ${syncResult.error ?? 'erro desconhecido'}. Use "Sincronizar com API" no Dashboard.`,
          'Atenção'
        );
      }
    });
  }, [showAlert]);

  const refresh = () => {
    setEstabelecimentos(getEstabelecimentos());
    setCategories(getEstabelecimentoCategories());
    setSubcategories(getEstabelecimentoSubcategories());
    setCidades(getCidades());
    setComercioUsers(getComercioRestauranteUsers());
  };

  const subcategoriesForForm = useMemo(
    () => (form.categoryId ? getEstabelecimentoSubcategoriesByCategory(form.categoryId) : []),
    [form.categoryId, subcategories]
  );

  const tagsDisponiveisEstabelecimento = useMemo(() => {
    void tagsPickerTickEst;
    const fromStorage = getEstabelecimentoTags();
    const setTags = new Set(fromStorage);
    estabelecimentos.forEach((e) => (e.tags ?? []).forEach((t) => setTags.add(t)));
    return Array.from(setTags).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [tagsPickerTickEst, estabelecimentos]);

  const estabelecimentosListaFiltrada = useMemo(() => {
    const qNome = listaFilterNome.trim().toLowerCase();
    return estabelecimentos.filter((est) => {
      if (listaFilterStatus) {
        const modo = getModoEstabelecimentoFromItem(est);
        if (modo !== listaFilterStatus) return false;
      }
      if (qNome && !est.nome.toLowerCase().includes(qNome)) return false;
      if (listaFilterCategoryId && est.categoryId !== listaFilterCategoryId) return false;
      if (listaFilterCityId && est.cityId !== listaFilterCityId) return false;
      const pub = est.publicacaoStatus ?? 'publicado';
      if (listaFilterPublicacao === '') {
        if (pub === 'lixeira') return false;
      } else if (listaFilterPublicacao === 'rascunho') {
        if (pub !== 'rascunho') return false;
      } else if (listaFilterPublicacao === 'publicado') {
        if (pub !== 'publicado') return false;
      } else if (listaFilterPublicacao === 'lixeira') {
        if (pub !== 'lixeira') return false;
      }
      return true;
    });
  }, [
    estabelecimentos,
    listaFilterStatus,
    listaFilterNome,
    listaFilterCategoryId,
    listaFilterCityId,
    listaFilterPublicacao,
  ]);

  const limparFiltrosListaEstabelecimentos = () => {
    setListaFilterStatus('');
    setListaFilterNome('');
    setListaFilterCategoryId('');
    setListaFilterCityId('');
    setListaFilterPublicacao('');
  };

  const modoEstabelecimentoAtual = useMemo(() => getModoEstabelecimentoFromForm(form), [form]);

  const googleMapsApiKey = ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_GOOGLE_MAPS_API_KEY ?? '').trim();
  const selectedCity = form.cityId ? cidades.find((c) => c.id === form.cityId) : null;
  const locCoords = useMemo(() => {
    if (form.lat != null && form.lng != null && Number.isFinite(form.lat) && Number.isFinite(form.lng)) {
      return { lat: Number(form.lat), lng: Number(form.lng) };
    }
    if (selectedCity && Number.isFinite(selectedCity.lat) && Number.isFinite(selectedCity.lng)) {
      return { lat: Number(selectedCity.lat), lng: Number(selectedCity.lng) };
    }
    return { lat: -14.235, lng: -51.9253 }; // Brasil
  }, [form.lat, form.lng, selectedCity]);

  const locZoom = useMemo(() => {
    if (form.lat != null && form.lng != null && Number.isFinite(form.lat) && Number.isFinite(form.lng)) {
      return form.mapsZoom ?? 16;
    }
    return 12;
  }, [form.lat, form.lng, form.mapsZoom]);

  useEffect(() => {
    if (viewMode !== 'form' || !googleMapsApiKey) return;
    (window as { gm_authFailure?: () => void }).gm_authFailure = () => {
      setMapsError('Chave da API do Google inválida ou restrita. Verifique o Google Cloud Console: ative as APIs Maps JavaScript e Places, confirme o faturamento e as restrições de domínio.');
      setMapsReady(false);
    };
    if ((window as { google?: { maps?: { places?: unknown } } }).google?.maps?.places) {
      setMapsReady(true);
      setMapsError(null);
      return;
    }
    const scriptId = 'google-maps-api-script-naviex';
    let existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    const w0 = (window as { google?: { maps?: { places?: unknown } } }).google;
    if (existing && w0?.maps && !w0.maps.places) {
      existing.remove();
      existing = null;
      try {
        delete (window as unknown as { google?: unknown }).google;
      } catch {
        /* ignore */
      }
    }
    if (existing) {
      existing.addEventListener('load', () => {
        setMapsReady(true);
        setMapsError(null);
      }, { once: true });
      existing.addEventListener('error', () => setMapsError('Não foi possível carregar o Google Maps.'), { once: true });
      if ((window as { google?: { maps?: unknown } }).google?.maps) {
        setMapsReady(true);
        setMapsError(null);
      }
      return;
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => { setMapsReady(true); setMapsError(null); };
    script.onerror = () => setMapsError('Não foi possível carregar o Google Maps.');
    document.head.appendChild(script);
  }, [googleMapsApiKey, viewMode]);

  const applyMapsLink = (
    link: string,
    coords: { lat: number; lng: number; zoom?: number },
    googleMeta?: {
      googlePlaceId?: string;
      formattedAddress?: string;
      source?: 'manual' | 'google_import';
      /** Se definido com id não vazio, define `cityId` e não corre geocode reverso. Omitir ou string vazia dispara geocode pelas coordenadas. */
      cidadeIdResolvida?: string;
      /**
       * Ao escolher sugestão do Google (nome ou mapa): sincroniza o nome do estabelecimento.
       * Omitir em colar link/coordenadas sem objeto Place.
       */
      nomeSincronizadoGoogle?: string;
    }
  ) => {
    setForm((f) => ({
      ...f,
      endereco: link,
      lat: coords.lat,
      lng: coords.lng,
      mapsZoom: coords.zoom ?? 16,
      ...(googleMeta?.cidadeIdResolvida !== undefined && googleMeta.cidadeIdResolvida !== ''
        ? { cityId: googleMeta.cidadeIdResolvida }
        : {}),
      ...(googleMeta?.nomeSincronizadoGoogle !== undefined ? { nome: googleMeta.nomeSincronizadoGoogle } : {}),
      ...(googleMeta?.googlePlaceId !== undefined ? { googlePlaceId: googleMeta.googlePlaceId } : {}),
      ...(googleMeta?.formattedAddress !== undefined ? { formattedAddress: googleMeta.formattedAddress } : {}),
      ...(googleMeta?.source !== undefined ? { source: googleMeta.source } : {}),
    }));
    setDirty(true);

    const cidadeJaResolvida =
      googleMeta?.cidadeIdResolvida !== undefined && googleMeta.cidadeIdResolvida !== '';
    if (!cidadeJaResolvida && cidades.length > 0) {
      void reverseGeocodeMatchCidadeCadastrada(coords.lat, coords.lng, cidades).then((m) => {
        setForm((f) => ({ ...f, cityId: m?.id ?? '' }));
        setDirty(true);
      });
    }
  };

  const applyMapsLinkRef = useRef(applyMapsLink);
  applyMapsLinkRef.current = applyMapsLink;

  type GooglePlacePick = {
    place_id?: string;
    formatted_address?: string;
    name?: string;
    url?: string;
    address_components?: GoogleAddressComponent[];
    geometry?: { location?: { lat: () => number; lng: () => number } };
  };

  const aplicarEstabelecimentoGooglePlaceSelecionado = useCallback((place: GooglePlacePick) => {
    const lat = place.geometry?.location?.lat();
    const lng = place.geometry?.location?.lng();
    if (lat === undefined || lng === undefined || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const m = matchCidadePorComponentesGoogle(place.address_components, cidades);
    const link =
      (place.url && place.url.trim()) || (place.formatted_address && place.formatted_address.trim()) || '';
    const nomeSync = nomeEstabelecimentoDoGooglePlace(place);
    applyMapsLinkRef.current(link || `https://www.google.com/maps?q=${lat},${lng}`, { lat, lng, zoom: 17 }, {
      googlePlaceId: place.place_id,
      formattedAddress: place.formatted_address,
      source: 'google_import',
      ...(m ? { cidadeIdResolvida: m.id } : {}),
      nomeSincronizadoGoogle: nomeSync,
    });
  }, [cidades]);

  useEffect(() => {
    if (formTab !== 'localizacao') return;
    const v = (form.endereco ?? '').trim();
    if (!v) {
      if (form.lat != null || form.lng != null) {
        setForm((f) => ({ ...f, lat: undefined, lng: undefined, mapsZoom: 16 }));
        setDirty(true);
      }
      return;
    }
    if (!looksLikeMapsLink(v) && !/^-?\d{1,3}\.\d+/.test(v)) {
      return;
    }
    const t = setTimeout(() => {
      parseGoogleMapsLink(v).then((coords) => {
        if (coords) applyMapsLink(v, coords);
      });
    }, 400);
    return () => clearTimeout(t);
  }, [formTab, form.endereco]);

  useEffect(() => {
    if (viewMode !== 'form' || formTab !== 'informacoes') {
      estabelecimentoNomeAutocompleteRef.current = null;
      return;
    }
    const g = window as unknown as {
      google?: {
        maps?: {
          places?: {
            Autocomplete: new (
              input: HTMLInputElement,
              opts?: Record<string, unknown>
            ) => {
              addListener: (ev: string, fn: () => void) => void;
              getPlace: () => GooglePlacePick;
            };
          };
        };
      };
    };
    const Places = g.google?.maps?.places;
    if (!mapsReady || !Places || !nomeInputRef.current || estabelecimentoNomeAutocompleteRef.current) return;

    const autocomplete = new Places.Autocomplete(nomeInputRef.current, {
      fields: ['place_id', 'formatted_address', 'geometry', 'name', 'url', 'address_components'],
      componentRestrictions: { country: 'br' },
    });
    autocomplete.addListener('place_changed', () => {
      aplicarEstabelecimentoGooglePlaceSelecionado(autocomplete.getPlace());
    });
    estabelecimentoNomeAutocompleteRef.current = autocomplete;
  }, [mapsReady, formTab, viewMode, aplicarEstabelecimentoGooglePlaceSelecionado]);

  useEffect(() => {
    if (viewMode !== 'form' || formTab !== 'localizacao') {
      estabelecimentoPlacesAutocompleteRef.current = null;
      return;
    }
    const g = window as unknown as {
      google?: {
        maps?: {
          places?: {
            Autocomplete: new (
              input: HTMLInputElement,
              opts?: Record<string, unknown>
            ) => {
              addListener: (ev: string, fn: () => void) => void;
              getPlace: () => GooglePlacePick;
            };
          };
        };
      };
    };
    const Places = g.google?.maps?.places;
    if (!mapsReady || !Places || !enderecoInputRef.current || estabelecimentoPlacesAutocompleteRef.current) return;

    const autocomplete = new Places.Autocomplete(enderecoInputRef.current, {
      fields: ['place_id', 'formatted_address', 'geometry', 'name', 'url', 'address_components'],
      componentRestrictions: { country: 'br' },
    });
    autocomplete.addListener('place_changed', () => {
      aplicarEstabelecimentoGooglePlaceSelecionado(autocomplete.getPlace());
    });
    estabelecimentoPlacesAutocompleteRef.current = autocomplete;
  }, [mapsReady, formTab, viewMode, aplicarEstabelecimentoGooglePlaceSelecionado]);

  const lastGeocodeRef = useRef<string | null>(null);
  const prevCityIdGeocodeRef = useRef(form.cityId);

  useEffect(() => {
    lastGeocodeRef.current = null;
  }, [catalogVersion]);

  useEffect(() => {
    const prev = prevCityIdGeocodeRef.current;
    prevCityIdGeocodeRef.current = form.cityId;
    if (prev && !form.cityId) {
      lastGeocodeRef.current = null;
    }
  }, [form.cityId]);

  /** Se já há coordenadas mas ainda sem cidade escolhida, tenta casar só com cidades cadastradas (geocode reverso). */
  useEffect(() => {
    if (!mapsReady) return;
    if (formTab !== 'localizacao' && formTab !== 'informacoes') return;
    if (form.lat == null || form.lng == null || !Number.isFinite(form.lat) || !Number.isFinite(form.lng)) return;
    if (form.cityId) return;
    if (cidades.length === 0) return;
    const key = `${form.lat.toFixed(6)},${form.lng.toFixed(6)}|n:${cidades.length}`;
    if (lastGeocodeRef.current === key) return;
    lastGeocodeRef.current = key;
    void reverseGeocodeMatchCidadeCadastrada(form.lat, form.lng, cidades).then((m) => {
      if (m) {
        setForm((f) => ({ ...f, cityId: m.id }));
        setDirty(true);
      }
    });
  }, [mapsReady, formTab, form.lat, form.lng, form.cityId, catalogVersion, cidades]);

  const removeGoogleErrorOverlay = () => {
    document.querySelectorAll('.gm-style-pbc, .gm-err-container, [class*="gm-err"], .gm-err-content').forEach((el) => el.remove());
    const err = Array.from(document.querySelectorAll('*')).find((el) => el.textContent?.includes('Esta página não carregou o Google Maps corretamente'));
    if (err) {
      const overlay = err.closest('[style*="position: fixed"]') || err.closest('[style*="position:fixed"]') || err.closest('.gm-style-pbc') || err;
      overlay?.remove();
    }
  };

  useEffect(() => {
    if (formTab !== 'localizacao') {
      mapRef.current = null;
      markerRef.current = null;
      removeGoogleErrorOverlay();
      const t = setTimeout(removeGoogleErrorOverlay, 300);
      const t2 = setTimeout(removeGoogleErrorOverlay, 800);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
    const iv = window.setInterval(removeGoogleErrorOverlay, 2000);
    return () => window.clearInterval(iv);
  }, [formTab]);

  useEffect(() => {
    if (formTab !== 'localizacao') return;
    if (!mapsReady || mapsError || !locCoords || !mapContainerRef.current) return;
    const w = window as unknown as { google?: { maps?: { Map: unknown; Marker: unknown; event?: { trigger: (obj: unknown, name: string) => void } } } };
    const googleMaps = w.google?.maps;
    if (!googleMaps?.Map || !googleMaps?.Marker) return;
    type EstMapInstance = {
      setCenter: (coords: { lat: number; lng: number }) => void;
      setZoom: (zoom: number) => void;
    };
    type EstMarkerInstance = { setPosition: (coords: { lat: number; lng: number }) => void };
    type EstMapCtor = new (
      el: HTMLElement,
      opts: {
        center: { lat: number; lng: number };
        zoom: number;
        mapTypeControl: boolean;
        streetViewControl: boolean;
        fullscreenControl: boolean;
      }
    ) => EstMapInstance;
    type EstMarkerCtor = new (opts: { map: EstMapInstance; position: { lat: number; lng: number } }) => EstMarkerInstance;
    const MapCtor = googleMaps.Map as EstMapCtor;
    const MarkerCtor = googleMaps.Marker as EstMarkerCtor;
    const zoom = locZoom;
    if (!mapRef.current) {
      const map = new MapCtor(mapContainerRef.current, {
        center: locCoords,
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      const marker = new MarkerCtor({ map, position: locCoords });
      mapRef.current = map;
      markerRef.current = marker;
    } else {
      mapRef.current.setCenter(locCoords);
      mapRef.current.setZoom(zoom);
      markerRef.current?.setPosition(locCoords);
    }
    const t = setTimeout(() => {
      if (mapRef.current && googleMaps?.event?.trigger) {
        googleMaps.event.trigger(mapRef.current, 'resize');
      }
    }, 100);
    return () => clearTimeout(t);
  }, [mapsReady, mapsError, locCoords, locZoom, formTab]);

  useEffect(() => {
    if (!statusComboContext && !listaStatusDropdown) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (statusComboRef.current?.contains(target)) return;
      if (target.closest('[data-est-lista-status-toggle="true"]')) return;
      if (target.closest('#estabelecimentos-lista-status-dropdown-portal')) return;
      setStatusComboContext(null);
      setListaStatusDropdown(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [statusComboContext, listaStatusDropdown]);

  useEffect(() => {
    if (!listaStatusDropdown) return;
    const estabelecimentoId = listaStatusDropdown.estabelecimentoId;
    const updatePosition = () => {
      const cur = listaStatusDropdownRef.current;
      if (!cur || cur.estabelecimentoId !== estabelecimentoId) return;
      const el = document.querySelector(
        `[data-est-lista-status-anchor="${estabelecimentoId}"]`
      ) as HTMLElement | null;
      if (!el) {
        setListaStatusDropdown(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setListaStatusDropdown((prev) =>
        prev && prev.estabelecimentoId === estabelecimentoId
          ? { ...prev, top: rect.bottom + 4, left: rect.left }
          : prev
      );
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [listaStatusDropdown?.estabelecimentoId]);

  useEffect(() => {
    refresh();
  }, []);

  useRefetchWhenCatalogChanges(() => refresh());

  const filteredUsers = useMemo(() => {
    const q = responsavelQuery.trim().toLowerCase();
    if (!q) return comercioUsers.slice(0, 10);
    return comercioUsers
      .filter((u) => !form.responsaveis.some((r) => r.userId === u.userId))
      .filter((u) => u.name.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q))
      .slice(0, 10);
  }, [comercioUsers, form.responsaveis, responsavelQuery]);

  const addResponsavel = (user: EstabelecimentoResponsavel) => {
    const profile = getComercioProfile(user.userId);
    setForm((f) => ({
      ...f,
      responsaveis: [...f.responsaveis, { ...user, phone: profile?.phone }],
    }));
    setResponsavelQuery('');
    setResponsavelOpen(false);
    setDirty(true);
  };

  const removeResponsavel = (userId: string) => {
    setForm((f) => ({
      ...f,
      responsaveis: f.responsaveis.filter((r) => r.userId !== userId),
    }));
    setDirty(true);
  };

  const toggleTagEstabelecimento = (tag: string) => {
    setForm((f) => {
      const tags = f.tags ?? [];
      const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
      return { ...f, tags: next };
    });
    setDirty(true);
  };

  const addNovaTagEstabelecimento = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    addEstabelecimentoTag(t);
    setTagsPickerTickEst((k) => k + 1);
    setForm((f) => {
      const tags = f.tags ?? [];
      if (tags.includes(t)) return f;
      return { ...f, tags: [...tags, t] };
    });
    setDirty(true);
    setTagInputEst('');
  };

  const saveNewCategory = async () => {
    if (!newCategoryName.trim()) {
      await showAlert('Informe o nome da nova categoria.');
      return;
    }
    try {
      const created = addEstabelecimentoCategory(newCategoryName);
      setCategoryModalOpen(false);
      setNewCategoryName('');
      refresh();
      schedulePushAdminSyncAfterMutation();
      if (viewMode === 'form') {
        setForm((f) => ({ ...f, categoryId: created.id, subcategoryIds: [] }));
        setDirty(true);
      } else {
        setCategoriasManagerOpen(true);
      }
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível cadastrar a categoria.');
    }
  };

  const saveNewSubcategory = async () => {
    if (!newSubcategoryCategoryId) {
      await showAlert('Selecione a categoria da nova subcategoria.');
      return;
    }
    if (!newSubcategoryName.trim()) {
      await showAlert('Informe o nome da nova subcategoria.');
      return;
    }
    try {
      const created = addEstabelecimentoSubcategory(newSubcategoryCategoryId, newSubcategoryName);
      setSubcategoryModalOpen(false);
      setNewSubcategoryCategoryId('');
      setNewSubcategoryName('');
      refresh();
      schedulePushAdminSyncAfterMutation();
      if (viewMode === 'form') {
        setForm((f) => ({
          ...f,
          categoryId: created.categoryId,
          subcategoryIds:
            f.categoryId === created.categoryId ? [...new Set([...f.subcategoryIds, created.id])] : [created.id],
        }));
        setDirty(true);
      } else {
        setCategoriasManagerOpen(true);
      }
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível cadastrar a subcategoria.');
    }
  };

  const handleDeleteCategory = async (cat: { id: string; name: string }) => {
    const result = await removeEstabelecimentoCategory(cat.id);
    if (result.ok) {
      refresh();
      schedulePushAdminSyncAfterMutation();
      setCategoriasManagerOpen(false);
      await showAlert(`Categoria "${cat.name}" excluída com sucesso.`, 'Sucesso');
      return;
    }
    setReassignModal({
      type: 'category',
      id: cat.id,
      name: cat.name,
      establishmentsCount: result.establishmentsCount,
    });
    setReassignNewCategoryId('');
    setReassignNewSubcategoryId('');
  };

  const handleDeleteSubcategory = async (sub: { id: string; name: string; categoryId: string }) => {
    const result = await removeEstabelecimentoSubcategory(sub.id);
    if (result.ok) {
      refresh();
      schedulePushAdminSyncAfterMutation();
      setCategoriasManagerOpen(false);
      await showAlert(`Subcategoria "${sub.name}" excluída com sucesso.`, 'Sucesso');
      return;
    }
    setReassignModal({
      type: 'subcategory',
      id: sub.id,
      name: sub.name,
      establishmentsCount: result.establishmentsCount,
    });
    setReassignNewCategoryId('');
    setReassignNewSubcategoryId('');
  };

  const confirmReassignAndDelete = async () => {
    if (!reassignModal) return;
    if (!reassignNewCategoryId || !reassignNewSubcategoryId) {
      await showAlert('Selecione a nova categoria e subcategoria para os itens afetados.');
      return;
    }
    const subsForCat = getEstabelecimentoSubcategoriesByCategory(reassignNewCategoryId);
    if (!subsForCat.some((s) => s.id === reassignNewSubcategoryId)) {
      await showAlert('A subcategoria selecionada não pertence à categoria escolhida.');
      return;
    }
    try {
      if (reassignModal.type === 'category') {
        await removeEstabelecimentoCategory(reassignModal.id, {
          newCategoryId: reassignNewCategoryId,
          newSubcategoryId: reassignNewSubcategoryId,
        });
      } else {
        await removeEstabelecimentoSubcategory(reassignModal.id, {
          newCategoryId: reassignNewCategoryId,
          newSubcategoryId: reassignNewSubcategoryId,
        });
      }
      refresh();
      schedulePushAdminSyncAfterMutation();
      setReassignModal(null);
      setCategoriasManagerOpen(false);
      await showAlert(
        `Itens reatribuídos e ${reassignModal.type === 'category' ? 'categoria' : 'subcategoria'} excluída com sucesso.`,
        'Sucesso'
      );
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao excluir.');
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({
      nome: '',
      subtitulo: '',
      resumo: '',
      descricao: '',
      cnpj: '',
      cityId: '',
      endereco: '',
      lat: undefined,
      lng: undefined,
      mapsZoom: 16,
      categoryId: '',
      subcategoryIds: [],
      responsaveis: [],
      statusPadrao: 'ativado',
      layoutType: 'normal',
      layoutExpiresAt: '',
      destaqueSubcategoria: false,
      destaqueSubcategoriaExpiresAt: '',
      destaqueNaviex: false,
      destaqueNaviexExpiresAt: '',
      periodosStatus: [],
      periodoDe: '',
      periodoAte: '',
      statusProgramadoHistorico: [],
      horarioSemanal: horarioPadrao(),
      excecoesCalendario: {},
      source: undefined,
      googlePlaceId: undefined,
      rating: undefined,
      totalRatings: undefined,
      formattedAddress: undefined,
      fotos: [],
      descontoParceriaDescricao: '',
      descontoMoradorAtivo: false,
      descontoMoradorModo: 'percent',
      descontoMoradorValor: '',
      descontoMoradorTexto: '',
      tags: [],
    });
    setTagInputEst('');
    setFormTab('informacoes');
    setViewMode('form');
    setDirty(false);
    isFirstFormRenderRef.current = true;
  };

  const applyModoStatusFromLista = (estabelecimentoId: string, modo: ModoEstabelecimentoOpcao) => {
    const current = getEstabelecimentoById(estabelecimentoId);
    if (!current) {
      void showAlert('Estabelecimento não encontrado para atualizar o status.');
      return;
    }
    try {
      const saved = saveEstabelecimento(patchEstabelecimentoForModoStatus(current, modo));
      setEstabelecimentos((prev) => prev.map((item) => (item.id === estabelecimentoId ? saved : item)));
      setListaStatusDropdown(null);
      schedulePushAdminSyncAfterMutation();
    } catch (error) {
      void showAlert(error instanceof Error ? error.message : 'Não foi possível salvar o status.');
    }
  };

  const openEdit = (id: string) => {
    const est = getEstabelecimentoById(id);
    if (!est) return;
    setEditingId(id);
    setForm({
      nome: est.nome,
      subtitulo: est.subtitulo ?? '',
      resumo: est.resumo ?? '',
      descricao: est.descricao ?? '',
      cnpj: est.cnpj ? formatCnpj(est.cnpj.replace(/\D/g, '')) : '',
      cityId: est.cityId ?? '',
      endereco: est.endereco ?? '',
      lat: est.lat,
      lng: est.lng,
      mapsZoom: 16,
      categoryId: est.categoryId ?? '',
      subcategoryIds: mergeEstabelecimentoSubcategoryIds(est),
      responsaveis: est.responsaveis ?? [],
      statusPadrao: (est.statusPadrao === 'desativado' ? 'desativado' : 'ativado') as 'ativado' | 'desativado',
      layoutType: (est.layoutType ?? 'normal') as EstabelecimentoLayoutType,
      layoutExpiresAt: est.layoutExpiresAt ?? '',
      destaqueSubcategoria: est.destaqueSubcategoria ?? false,
      destaqueSubcategoriaExpiresAt: est.destaqueSubcategoriaExpiresAt ?? '',
      destaqueNaviex: est.destaqueNaviex ?? false,
      destaqueNaviexExpiresAt: est.destaqueNaviexExpiresAt ?? '',
      periodosStatus: (est.periodosStatus ?? []).filter((p) => p.ate >= hojeYmdLocal()),
      periodoDe: '',
      periodoAte: '',
      statusProgramadoHistorico: est.statusProgramadoHistorico ?? [],
      horarioSemanal: est.horarioSemanal ?? horarioPadrao(),
      excecoesCalendario: est.excecoesCalendario ?? {},
      source: est.source,
      googlePlaceId: est.googlePlaceId,
      rating: est.rating,
      totalRatings: est.totalRatings,
      formattedAddress: est.formattedAddress,
      fotos: (est.fotos ?? []).slice(0, MAX_FOTOS_ESTABELECIMENTO),
      descontoParceriaDescricao: est.descontoParceriaDescricao ?? '',
      descontoMoradorAtivo: est.descontoMoradorAtivo === true,
      descontoMoradorModo: (est.descontoMoradorModo === 'text' || est.descontoMoradorModo === 'currency'
        ? est.descontoMoradorModo
        : 'percent') as DescontoMoradorModo,
      descontoMoradorValor: est.descontoMoradorValor ?? '',
      descontoMoradorTexto: est.descontoMoradorTexto ?? '',
      tags: [...(est.tags ?? [])],
    });
    setTagInputEst('');
    setFormTab('informacoes');
    setViewMode('form');
    setDirty(false);
    isFirstFormRenderRef.current = true;
  };

  const fetchImportPlaces = async () => {
    const city = cidades.find((c) => c.id === importCityId);
    if (!city?.cidade || !importType) {
      await showAlert('Selecione a cidade e o tipo.');
      return;
    }
    setImportLoading(true);
    setImportResults([]);
    setImportSelected(new Set());
    try {
      const res = await fetch(apiUrl('/api/admin/import-google-places'), {
        method: 'POST',
        headers: adminApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ city: city.cidade, type: importType }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string; details?: string };
        const msg = err.error ?? `Erro ${res.status}`;
        throw new Error(err.details ? `${msg}: ${err.details}` : msg);
      }
      const data = (await res.json()) as { places?: ImportPlaceResult[] };
      setImportResults(data.places ?? []);
      if ((data.places ?? []).length === 0) {
        await showAlert('Nenhum resultado encontrado.');
      }
    } catch (e) {
      await showAlert(e instanceof Error ? e.message : 'Erro ao buscar do Google Places.');
    } finally {
      setImportLoading(false);
    }
  };

  const applyImportToForm = (place: ImportPlaceResult) => {
    const cityId = importCityId || (cidades[0]?.id ?? '');
    const categoryId = categories[0]?.id ?? '';
    const subsPrimeira = categoryId ? getEstabelecimentoSubcategoriesByCategory(categoryId) : [];
    const primeiraSub = subsPrimeira[0]?.id ?? '';
    const descImport = (place.descricao ?? '').trim();
    setForm((f) => ({
      ...f,
      nome: place.nome ?? f.nome,
      descricao: place.descricao ?? f.descricao,
      resumo: descImport ? descImport.slice(0, 500) : f.resumo,
      cnpj: '',
      cityId,
      endereco: place.endereco ?? place.formattedAddress ?? f.endereco,
      lat: place.lat ?? f.lat,
      lng: place.lng ?? f.lng,
      formattedAddress: place.formattedAddress ?? f.formattedAddress,
      source: 'google_import',
      googlePlaceId: place.googlePlaceId ?? f.googlePlaceId,
      rating: place.rating ?? f.rating,
      totalRatings: place.totalRatings ?? f.totalRatings,
      categoryId: categoryId || f.categoryId,
      subcategoryIds: primeiraSub ? [primeiraSub] : [],
    }));
    setImportModalOpen(false);
    setFormTab('informacoes');
    setViewMode('form');
    setDirty(true);
    isFirstFormRenderRef.current = true;
  };

  const isGoogleImport = form.source === 'google_import';

  const publicacaoIssues = useMemo(
    () => buildEstabelecimentoPublicacaoIssues(form, isGoogleImport, cidades),
    [form, isGoogleImport, cidades]
  );

  /** Publicar e atualizar (cadastro já publicado) usam o mesmo fluxo: `saveEstPublishedForLeave`. */
  const textoBotaoPrincipalForm = 'Publicar';

  type EstabelecimentoPayload = Omit<Estabelecimento, 'id' | 'createdAt' | 'updatedAt'>;

  const buildPayloadOrError = (f: typeof form, googleImport: boolean): { error: string } | { payload: EstabelecimentoPayload } => {
    if (!f.nome.trim()) return { error: 'Nome é obrigatório.' };
    if (!f.resumo.trim()) return { error: 'Resumo é obrigatório.' };
    if (!htmlToPlainText(f.descricao)) return { error: 'Descrição é obrigatória.' };
    const cnpjDigits = f.cnpj.trim().replace(/\D/g, '');
    if (!googleImport) {
      if (cnpjDigits.length === 0) return { error: 'CNPJ é obrigatório para cadastro manual.' };
      if (cnpjDigits.length !== 14) return { error: 'CNPJ deve ter 14 dígitos.' };
      if (!isValidCnpj(f.cnpj)) return { error: 'CNPJ inválido. Verifique os dígitos.' };
    }
    if (!f.cityId) return { error: 'Cidade é obrigatória.' };
    if (!f.categoryId) return { error: 'Categoria é obrigatória.' };
    const subs = f.categoryId ? getEstabelecimentoSubcategoriesByCategory(f.categoryId) : [];
    const validSubIds = f.subcategoryIds.filter((id) => subs.some((s) => s.id === id));
    if (subs.length > 0 && validSubIds.length === 0) return { error: 'Selecione pelo menos uma subcategoria para esta categoria.' };
    const temEnderecoOuCoords = ((f.endereco ?? '').trim().length > 0) || (f.lat != null && f.lng != null);
    if (!temEnderecoOuCoords) return { error: 'Link do Google Maps ou coordenadas são obrigatórios.' };
    const cidadeExiste = f.cityId ? cidades.some((c) => c.id === f.cityId) : false;
    if (!f.cityId || !cidadeExiste) {
      return { error: 'Selecione uma cidade cadastrada. Cadastre a cidade em Cidades antes de adicionar o estabelecimento.' };
    }
    if (f.descontoMoradorAtivo) {
      const modo = (f.descontoMoradorModo ?? 'percent') as DescontoMoradorModo;
      if (modo === 'text') {
        if (!f.descontoMoradorTexto.trim()) {
          return { error: 'Desconto morador: preencha o texto descritivo.' };
        }
      } else {
        const n = parseValorDescontoMoradorNumero(f.descontoMoradorValor);
        if (n == null) {
          return { error: 'Desconto morador: informe um valor numérico válido para % ou R$.' };
        }
        if (modo === 'percent' && n > 100) {
          return { error: 'Desconto morador: o percentual não pode exceder 100%.' };
        }
      }
    }
    const hoje = hojeYmdLocal();
    const periodosVigentes = (f.periodosStatus ?? []).filter((p) => p.ate >= hoje);
    const principalOn = f.layoutType === 'matéria_principal' || f.layoutType === 'slide_principal';
    const fotosCap = (f.fotos ?? []).slice(0, MAX_FOTOS_ESTABELECIMENTO);
    return {
      payload: {
        nome: f.nome.trim(),
        subtitulo: f.subtitulo.trim() || undefined,
        resumo: f.resumo.trim(),
        descricao: f.descricao.trim(),
        cnpj: cnpjDigits || undefined,
        cityId: f.cityId,
        endereco: f.endereco.trim() || undefined,
        lat: f.lat,
        lng: f.lng,
        categoryId: f.categoryId,
        subcategoryIds: validSubIds.length > 0 ? validSubIds : undefined,
        subcategoryId: validSubIds[0] ?? '',
        responsaveis: f.responsaveis,
        statusPadrao: f.statusPadrao,
        periodosStatus: periodosVigentes.length > 0 ? periodosVigentes : undefined,
        statusProgramadoHistorico:
          (f.statusProgramadoHistorico ?? []).length > 0 ? f.statusProgramadoHistorico : undefined,
        layoutType: principalOn ? f.layoutType : undefined,
        layoutExpiresAt:
          principalOn && toDateInputValue(f.layoutExpiresAt)
            ? mergeValidityDateWithSaveTimeNow(toDateInputValue(f.layoutExpiresAt))
            : undefined,
        destaqueSubcategoria: f.destaqueSubcategoria || undefined,
        destaqueSubcategoriaExpiresAt:
          f.destaqueSubcategoria && toDateInputValue(f.destaqueSubcategoriaExpiresAt)
            ? mergeValidityDateWithSaveTimeNow(toDateInputValue(f.destaqueSubcategoriaExpiresAt))
            : undefined,
        destaqueNaviex: f.destaqueNaviex || undefined,
        destaqueNaviexExpiresAt:
          f.destaqueNaviex && toDateInputValue(f.destaqueNaviexExpiresAt)
            ? mergeValidityDateWithSaveTimeNow(toDateInputValue(f.destaqueNaviexExpiresAt))
            : undefined,
        horarioSemanal: f.horarioSemanal,
        excecoesCalendario: f.excecoesCalendario,
        source: f.source,
        googlePlaceId: f.googlePlaceId,
        rating: f.rating,
        totalRatings: f.totalRatings,
        formattedAddress: f.formattedAddress,
        fotos: fotosCap.length ? fotosCap : undefined,
        descontoParceriaDescricao: f.descontoParceriaDescricao.trim() || undefined,
        tags: (f.tags ?? []).length > 0 ? f.tags : undefined,
        ...(f.descontoMoradorAtivo
          ? (() => {
              const modo = (f.descontoMoradorModo ?? 'percent') as DescontoMoradorModo;
              if (modo === 'text') {
                return {
                  descontoMoradorAtivo: true,
                  descontoMoradorModo: 'text' as const,
                  descontoMoradorTexto: f.descontoMoradorTexto.trim(),
                  descontoMoradorValor: undefined,
                };
              }
              return {
                descontoMoradorAtivo: true,
                descontoMoradorModo: modo,
                descontoMoradorValor: f.descontoMoradorValor.trim(),
                descontoMoradorTexto: undefined,
              };
            })()
          : {
              descontoMoradorAtivo: false,
              descontoMoradorModo: undefined,
              descontoMoradorValor: undefined,
              descontoMoradorTexto: undefined,
            }),
      },
    };
  };

  /** Gravação como rascunho: mesmos campos que publicado, com valores mínimos onde faltar obrigatório. */
  const buildEstabelecimentoDraftPayload = (): EstabelecimentoPayload => {
    const f = form;
    const hoje = hojeYmdLocal();
    const periodosVigentes = (f.periodosStatus ?? []).filter((p) => p.ate >= hoje);
    const principalOn = f.layoutType === 'matéria_principal' || f.layoutType === 'slide_principal';
    const fotosCap = (f.fotos ?? []).slice(0, MAX_FOTOS_ESTABELECIMENTO);
    const nome = f.nome.trim() || 'Rascunho sem nome';
    const resumo = f.resumo.trim() || '—';
    const descricao = htmlToPlainText(f.descricao) ? f.descricao.trim() : '—';
    const cnpjDigits = f.cnpj.trim().replace(/\D/g, '');
    const googleImport = f.source === 'google_import';
    let cityId = f.cityId;
    if (cityId && !cidades.some((c) => c.id === cityId)) cityId = '';
    if (!cityId && cidades[0]) cityId = cidades[0].id;
    const subs = f.categoryId ? getEstabelecimentoSubcategoriesByCategory(f.categoryId) : [];
    let subIdsDraft = f.subcategoryIds.filter((id) => subs.some((s) => s.id === id));
    if (subs.length > 0 && subIdsDraft.length === 0 && subs[0]) subIdsDraft = [subs[0].id];
    const temLoc = ((f.endereco ?? '').trim().length > 0) || (f.lat != null && f.lng != null);
    const enderecoOut = (f.endereco ?? '').trim() || (temLoc ? undefined : '—');
    const cnpjOut =
      googleImport
        ? cnpjDigits.length === 14 && isValidCnpj(f.cnpj)
          ? cnpjDigits
          : undefined
        : cnpjDigits.length === 14 && isValidCnpj(f.cnpj)
          ? cnpjDigits
          : undefined;
    return {
      nome,
      subtitulo: f.subtitulo.trim() || undefined,
      resumo,
      descricao,
      cnpj: cnpjOut,
      cityId: cityId || undefined,
      endereco: enderecoOut,
      lat: f.lat,
      lng: f.lng,
      categoryId: f.categoryId || undefined,
      subcategoryIds: subIdsDraft.length > 0 ? subIdsDraft : undefined,
      subcategoryId: subIdsDraft[0] ?? '',
      responsaveis: f.responsaveis,
      statusPadrao: f.statusPadrao,
      periodosStatus: periodosVigentes.length > 0 ? periodosVigentes : undefined,
      statusProgramadoHistorico:
        (f.statusProgramadoHistorico ?? []).length > 0 ? f.statusProgramadoHistorico : undefined,
      layoutType: principalOn ? f.layoutType : undefined,
      layoutExpiresAt:
        principalOn && toDateInputValue(f.layoutExpiresAt)
          ? mergeValidityDateWithSaveTimeNow(toDateInputValue(f.layoutExpiresAt))
          : undefined,
      destaqueSubcategoria: f.destaqueSubcategoria || undefined,
      destaqueSubcategoriaExpiresAt:
        f.destaqueSubcategoria && toDateInputValue(f.destaqueSubcategoriaExpiresAt)
          ? mergeValidityDateWithSaveTimeNow(toDateInputValue(f.destaqueSubcategoriaExpiresAt))
          : undefined,
      destaqueNaviex: f.destaqueNaviex || undefined,
      destaqueNaviexExpiresAt:
        f.destaqueNaviex && toDateInputValue(f.destaqueNaviexExpiresAt)
          ? mergeValidityDateWithSaveTimeNow(toDateInputValue(f.destaqueNaviexExpiresAt))
          : undefined,
      horarioSemanal: f.horarioSemanal,
      excecoesCalendario: f.excecoesCalendario,
      source: f.source,
      googlePlaceId: f.googlePlaceId,
      rating: f.rating,
      totalRatings: f.totalRatings,
      formattedAddress: f.formattedAddress,
      fotos: fotosCap.length ? fotosCap : undefined,
      descontoParceriaDescricao: f.descontoParceriaDescricao.trim() || undefined,
      tags: (f.tags ?? []).length > 0 ? f.tags : undefined,
      ...(f.descontoMoradorAtivo
        ? (() => {
            const modo = (f.descontoMoradorModo ?? 'percent') as DescontoMoradorModo;
            if (modo === 'text') {
              return {
                descontoMoradorAtivo: true,
                descontoMoradorModo: 'text' as const,
                descontoMoradorTexto: f.descontoMoradorTexto.trim(),
                descontoMoradorValor: undefined,
              };
            }
            return {
              descontoMoradorAtivo: true,
              descontoMoradorModo: modo,
              descontoMoradorValor: f.descontoMoradorValor.trim(),
              descontoMoradorTexto: undefined,
            };
          })()
        : {
            descontoMoradorAtivo: false,
            descontoMoradorModo: undefined,
            descontoMoradorValor: undefined,
            descontoMoradorTexto: undefined,
          }),
    };
  };

  const handleImageFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
    const toProcess = Array.from(files).filter((file) => allowed.includes(file.type));
    if (toProcess.length === 0) {
      void showAlert('Use apenas PNG ou JPG.', 'Aviso');
      return;
    }
    const current = form.fotos?.length ?? 0;
    const slots = MAX_FOTOS_ESTABELECIMENTO - current;
    if (slots <= 0) {
      void showAlert(`Limite de ${MAX_FOTOS_ESTABELECIMENTO} fotos. Remova alguma antes de adicionar outras.`, 'Aviso');
      return;
    }
    const batch = toProcess.slice(0, slots);
    if (toProcess.length > slots) {
      void showAlert(`Só é possível adicionar mais ${slots} foto(s). As primeiras ${slots} serão enviadas.`, 'Aviso');
    }
    void (async () => {
      setImagemUploadBusy(true);
      const refs: string[] = [];
      try {
        for (const file of batch) {
          const dataUrl = await compressImageFileForStorage(file, { maxSide: 1920, quality: 0.82 });
          const blob = await (await fetch(dataUrl)).blob();
          const jpegFile = new File([blob], 'estabelecimento.jpg', { type: blob.type || 'image/jpeg' });
          const { reference } = await uploadAdminMedia(jpegFile, 'estabelecimentos');
          refs.push(reference);
        }
        setForm((prev) => ({ ...prev, fotos: [...(prev.fotos ?? []), ...refs] }));
        setDirty(true);
      } catch (e) {
        await showAlert(
          e instanceof Error ? e.message : 'Não foi possível enviar as imagens. Verifique se a API está no ar e o bucket S3 configurado.',
          'Erro ao enviar'
        );
      } finally {
        setImagemUploadBusy(false);
      }
    })();
  };

  const reorderFotos = useCallback((fromIndex: number, toIndex: number) => {
    setForm((prev) => {
      const fotos = [...(prev.fotos ?? [])];
      if (
        fromIndex < 0
        || toIndex < 0
        || fromIndex >= fotos.length
        || toIndex >= fotos.length
        || fromIndex === toIndex
      ) {
        return prev;
      }
      const [moved] = fotos.splice(fromIndex, 1);
      fotos.splice(toIndex, 0, moved);
      return { ...prev, fotos };
    });
    setDirty(true);
  }, [setDirty]);

  const handleFotoDrop = useCallback((targetIndex: number) => {
    if (draggingFotoIndex === null) return;
    reorderFotos(draggingFotoIndex, targetIndex);
    setDraggingFotoIndex(null);
    setDragOverFotoIndex(null);
  }, [draggingFotoIndex, reorderFotos]);

  const resetFotoDragState = useCallback(() => {
    setDraggingFotoIndex(null);
    setDragOverFotoIndex(null);
  }, []);

  const handleImportarFotos = async () => {
    let mapsUrl = (form.endereco ?? '').trim();
    let placeId =
      (form.googlePlaceId?.replace(/^places\//, '') ?? '').trim() || extractPlaceIdFromUrl(mapsUrl);
    if (!placeId) {
      const pasted = typeof window !== 'undefined' ? window.prompt('Cole o URL do Google Maps do lugar para importar fotos:') : null;
      const t = (pasted ?? '').trim();
      if (!t) return;
      mapsUrl = t;
      placeId = extractPlaceIdFromUrl(mapsUrl);
      if (!placeId) {
        await showAlert('Esse texto não parece um link válido do Google Maps. Use o URL completo (barra de endereço ou Partilhar).');
        return;
      }
      setForm((f) => ({ ...f, endereco: mapsUrl }));
      setDirty(true);
    }
    const current = form.fotos?.length ?? 0;
    const slots = MAX_FOTOS_ESTABELECIMENTO - current;
    if (slots <= 0) {
      await showAlert(`Limite de ${MAX_FOTOS_ESTABELECIMENTO} fotos. Remova alguma antes de importar.`, 'Aviso');
      return;
    }
    const cidadeNome = selectedCity?.cidade?.trim() ?? '';
    const fallbackQuery = [form.nome?.trim(), cidadeNome].filter(Boolean).join(' ').trim();
    setImportFotosLoading(true);
    try {
      const { fotos } = await fetchPlacePhotos(placeId || '', fallbackQuery || undefined);
      if (!fotos.length) {
        await showAlert('Nenhuma foto encontrada para este lugar.');
        return;
      }
      const processed: string[] = [];
      for (let i = 0; i < fotos.length && processed.length < slots; i++) {
        try {
          const dataUrl = await processImageAutoAspect(fotos[i]);
          const blob = await (await fetch(dataUrl)).blob();
          const jpegFile = new File([blob], `google-${i}.jpg`, { type: blob.type || 'image/jpeg' });
          const { reference } = await uploadAdminMedia(jpegFile, 'estabelecimentos');
          processed.push(reference);
        } catch {
          try {
            const res = await fetch(fotos[i]);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const jpegFile = new File([blob], `google-${i}.jpg`, { type: blob.type || 'image/jpeg' });
            const { reference } = await uploadAdminMedia(jpegFile, 'estabelecimentos');
            processed.push(reference);
          } catch {
            /* ignora esta foto */
          }
        }
      }
      if (processed.length === 0) {
        await showAlert('Não foi possível enviar nenhuma foto para o armazenamento. Verifique a API e o bucket S3.');
        return;
      }
      const mergedFotos = [...(form.fotos ?? []), ...processed].slice(0, MAX_FOTOS_ESTABELECIMENTO);
      setForm((prev) => ({ ...prev, fotos: mergedFotos }));
      setDirty(true);

      const fMerged = { ...form, fotos: mergedFotos };
      const built = buildPayloadOrError(fMerged, fMerged.source === 'google_import');
      let saved = false;
      if (editingId && 'payload' in built) {
        const curr = getEstabelecimentoById(editingId);
        if (curr) {
          saveEstabelecimento({ ...curr, ...built.payload });
          saved = true;
          setDirty(false);
          refresh();
          schedulePushAdminSyncAfterMutation();
        }
      }

      await showAlert(
        saved
          ? `${processed.length} foto(s) importada(s), enviadas para a nuvem e guardadas automaticamente.`
          : editingId && 'error' in built
            ? `${processed.length} foto(s) importada(s). Não foi possível guardar automaticamente: ${built.error} Use Publicar ou Rascunho quando o formulário estiver válido.`
            : `${processed.length} foto(s) importada(s) e enviadas para a nuvem.${editingId ? '' : ' Clique em Publicar ou em Rascunho para persistir.'}`
      );
    } catch (e) {
      await showAlert(e instanceof Error ? e.message : 'Erro ao importar fotos. Verifique se o endpoint /api/admin/place-photos está disponível.');
    } finally {
      setImportFotosLoading(false);
    }
  };

  const persistEstabelecimentoDraftSilent = (opts?: { keepOnForm?: boolean; skipDeferredSync?: boolean }): boolean => {
    try {
      const draftPayload = buildEstabelecimentoDraftPayload();
      if (editingId) {
        const curr = getEstabelecimentoById(editingId);
        if (!curr) return false;
        saveEstabelecimento({ ...curr, ...draftPayload, publicacaoStatus: 'rascunho' });
      } else {
        const added = addEstabelecimento({ ...draftPayload, publicacaoStatus: 'rascunho' });
        if (opts?.keepOnForm) setEditingId(added.id);
      }
    } catch {
      return false;
    }
    setDirty(false);
    refresh();
    if (!opts?.skipDeferredSync) schedulePushAdminSyncAfterMutation();
    if (!opts?.keepOnForm) {
      setViewMode('list');
      setEditingId(null);
    }
    return true;
  };

  const saveEstPublishedForLeave = async (): Promise<boolean> => {
    const isNovoCadastro = !editingId;
    const built = buildPayloadOrError(form, isGoogleImport);
    if ('error' in built) {
      const detalhe =
        publicacaoIssues.length > 0
          ? publicacaoIssues.join('\n• ')
          : built.error;
      await showAlert(
        'Não é possível publicar (atualizar) com campos obrigatórios incompletos:\n\n• ' + detalhe
      );
      return false;
    }
    const { payload } = built;
    if (periodosStatusTemSobreposicao(form.periodosStatus ?? [])) {
      const ok = await showConfirm(MSG_AVISO_PERIODOS_SOBREPOSTOS, {
        title: 'Períodos sobrepostos',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      });
      if (!ok) return false;
    }
    if (editingId) {
      const curr = getEstabelecimentoById(editingId);
      if (!curr) {
        await showAlert('Estabelecimento não encontrado.');
        return false;
      }
      saveEstabelecimento({ ...curr, ...payload, publicacaoStatus: 'publicado' });
    } else {
      addEstabelecimento({ ...payload, publicacaoStatus: 'publicado' });
    }
    setDirty(false);
    refresh();
    const syncRes = await pushAdminSyncToApi();
    if (!syncRes.ok) {
      await showAlert(
        `Alterações guardadas neste navegador, mas a sincronização com o servidor falhou: ${syncRes.error ?? 'erro desconhecido'}. O estado pode voltar ao rascunho se o catálogo for recarregado da API antes de corrigir. Use «Sincronizar com API» no Dashboard ou tente publicar de novo.`,
        'Atenção'
      );
    } else {
      const reloadRes = await reloadCatalog();
      if (!reloadRes.ok) {
        await showAlert(
          `Gravado na API, mas ao atualizar o painel: ${reloadRes.error}. Atualize a página (F5).`,
          'Atenção'
        );
      } else {
        if (isNovoCadastro) {
          await showAlert('Cadastro realizado com sucesso!', 'Sucesso', 'Continuar', 1000);
        } else {
          await showAlert('Alterações publicadas e sincronizadas com o servidor.', 'Sucesso');
        }
      }
    }
    setViewMode('list');
    setEditingId(null);
    return true;
  };

  const handlePublishSave = async () => {
    if (publicacaoIssues.length > 0) {
      await showAlert(
        'Preencha os campos obrigatórios para publicar ou atualizar:\n\n• ' + publicacaoIssues.join('\n• ')
      );
      return;
    }
    const okLeave = await saveEstPublishedForLeave();
    if (!okLeave) return;
  };

  persistEstDraftRef.current = persistEstabelecimentoDraftSilent;
  saveEstPublishedRef.current = saveEstPublishedForLeave;

  useEffect(() => {
    const state = location.state as { fromNav?: boolean } | null;
    if (state?.fromNav && location.pathname === '/estabelecimentos') {
      setViewMode('list');
      setEditingId(null);
      setDirty(false);
      isFirstFormRenderRef.current = true;
      navigate('/estabelecimentos', { replace: true, state: {} });
    }
  }, [location.pathname, location.state, setDirty, navigate]);

  useEffect(() => {
    if (viewMode !== 'form') {
      isFirstFormRenderRef.current = true;
      return;
    }
    if (isFirstFormRenderRef.current) {
      isFirstFormRenderRef.current = false;
      return;
    }
    setDirty(true);
  }, [form, viewMode, setDirty]);

  useEffect(() => {
    if (viewMode !== 'form') {
      setIsPublishedLeave(false);
      setSkipAutoFlushOnLeave(false);
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
      setSaveDraftHandler(null);
      return;
    }
    const storedPub = editingId ? (getEstabelecimentoById(editingId)?.publicacaoStatus ?? 'publicado') : 'rascunho';
    const isRascunho = storedPub === 'rascunho';
    setIsPublishedLeave(Boolean(isDirty && (storedPub === 'publicado' || storedPub === 'lixeira')));
    setSkipAutoFlushOnLeave(isRascunho);
    setFlushDraftHandler(null);
    setSaveDraftHandler(
      isRascunho && isDirty && Boolean(editingId)
        ? async () => Promise.resolve(persistEstDraftRef.current({ keepOnForm: false }))
        : null
    );
    setSavePublishedHandler(
      (storedPub === 'publicado' || storedPub === 'lixeira') && isDirty
        ? async () => saveEstPublishedRef.current()
        : null
    );
    return () => {
      setSkipAutoFlushOnLeave(false);
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
      setSaveDraftHandler(null);
    };
  }, [
    viewMode,
    editingId,
    isDirty,
    setIsPublishedLeave,
    setSkipAutoFlushOnLeave,
    setFlushDraftHandler,
    setSavePublishedHandler,
    setSaveDraftHandler,
  ]);

  useEffect(() => {
    if (viewMode !== 'form') {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    if (!editingId) {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    const storedPub = editingId ? (getEstabelecimentoById(editingId)?.publicacaoStatus ?? 'publicado') : 'rascunho';
    if (storedPub !== 'rascunho') {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    if (isFirstFormRenderRef.current) return;
    if (!isDirty) return;
    if (autoDraftTimerRef.current) clearTimeout(autoDraftTimerRef.current);
    autoDraftTimerRef.current = setTimeout(() => {
      autoDraftTimerRef.current = null;
      void persistEstDraftRef.current({ keepOnForm: true });
    }, 1200);
    return () => {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
    };
  }, [form, viewMode, editingId, isDirty]);

  useEffect(() => {
    if (viewMode !== 'form') return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      if (!editingId) return;
      const est = editingId ? getEstabelecimentoById(editingId) : null;
      const pub = est?.publicacaoStatus ?? 'publicado';
      if (pub === 'publicado' || pub === 'lixeira') {
        e.preventDefault();
        e.returnValue = '';
        return;
      }
      void persistEstDraftRef.current({ keepOnForm: true });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [viewMode, isDirty, editingId, form]);

  const handleCancel = () => {
    if (viewMode !== 'form') return;
    if (!isDirty) {
      setViewMode('list');
      setEditingId(null);
      return;
    }
    const est = editingId ? getEstabelecimentoById(editingId) : null;
    if ((est?.publicacaoStatus ?? 'publicado') === 'rascunho') {
      setRascunhoLeaveModalOpen(true);
      return;
    }
    setDirty(false);
    setViewMode('list');
    setEditingId(null);
  };

  const labelTipoProgramadoEstabelecimento = (tipo: string) => {
    switch (tipo) {
      case 'ativado':
        return 'Ativar';
      case 'desativado':
        return 'Desativado';
      case 'naviex':
        return 'Destaque Naviex';
      case 'principal':
        return 'Destaque Principal';
      case 'categoria':
        return 'Destaque Categoria';
      default:
        return tipo;
    }
  };

  const handleDelete = async (est: Estabelecimento) => {
    if (est.publicacaoStatus === 'lixeira') {
      const okDel = await showConfirm(`Apagar permanentemente «${est.nome}»? Esta ação não pode ser desfeita.`, {
        title: 'Apagar da lixeira',
        confirmText: 'Apagar permanentemente',
        cancelText: 'Cancelar',
      });
      if (!okDel) return;
      removeEstabelecimento(est.id);
    } else {
      const ok = await showConfirm(
        `Mover «${est.nome}» para a lixeira? Poderá filtrar por «Lixeira» na coluna Publicação e restaurar ao editar e publicar de novo.`,
        {
          title: 'Mover para a lixeira',
          confirmText: 'Mover para lixeira',
          cancelText: 'Cancelar',
        }
      );
      if (!ok) return;
      saveEstabelecimento({ ...est, publicacaoStatus: 'lixeira' });
    }
    if (editingId === est.id) {
      setEditingId(null);
      setViewMode('list');
      setDirty(false);
    }
    refresh();
    const syncRes = await pushAdminSyncToApi();
    if (!syncRes.ok) {
      await showAlert(
        `Alteração guardada neste navegador, mas a sincronização falhou: ${syncRes.error ?? 'erro desconhecido'}. Use «Sincronizar com API» no Dashboard.`,
        'Atenção'
      );
    } else {
      const reloadRes = await reloadCatalog();
      if (!reloadRes.ok) {
        await showAlert(`Sincronizado, mas ao atualizar o painel: ${reloadRes.error}. Atualize a página (F5).`, 'Atenção');
      }
    }
  };

  const adicionarPeriodoStatus = (tipo: 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria') => {
    let de = form.periodoDe?.trim() || '';
    let ate = form.periodoAte?.trim() || '';
    if (!de) return;
    if (!ate) ate = de;
    if (de > ate) [de, ate] = [ate, de];
    const umSoDia = de === ate;
    const quando = new Date().toISOString();

    if (umSoDia) {
      if (tipo === 'ativado' || tipo === 'desativado') {
        setForm((f) => ({
          ...f,
          statusPadrao: tipo,
          periodoDe: '',
          periodoAte: '',
          statusProgramadoHistorico: [
            ...(f.statusProgramadoHistorico ?? []),
            { quando, dia: de, tipo, modo: 'permanente' as const },
          ],
        }));
        setDirty(true);
        return;
      }
      const modo = tipo === 'naviex' ? 'naviex' : tipo === 'principal' ? 'principal' : 'categoria';
      setForm((f) => ({
        ...f,
        ...buildUpdateModoEstabelecimento(modo),
        periodoDe: '',
        periodoAte: '',
        statusProgramadoHistorico: [
          ...(f.statusProgramadoHistorico ?? []),
          { quando, dia: de, tipo, modo: 'permanente' as const },
        ],
      }));
      setDirty(true);
      return;
    }
    setForm((f) => ({
      ...f,
      periodosStatus: [...f.periodosStatus, { de, ate, tipo }].sort((a, b) => a.de.localeCompare(b.de)),
      periodoDe: '',
      periodoAte: '',
    }));
    setDirty(true);
  };

  const removerPeriodoStatus = (index: number) => {
    setForm((f) => ({ ...f, periodosStatus: f.periodosStatus.filter((_, i) => i !== index) }));
    setDirty(true);
  };

  const updateHorarioDia = (dia: DiaSemana, valor: HorarioSemanal[DiaSemana]) => {
    setForm((f) => ({
      ...f,
      horarioSemanal: { ...f.horarioSemanal, [dia]: valor },
    }));
    setDirty(true);
  };

  const cloneHorarioDia = (v: HorarioDia | null): HorarioDia | null => {
    if (v === null) return null;
    const o: HorarioDia = { abertura: v.abertura, fechamento: v.fechamento };
    if (v.intervalo) o.intervalo = { inicio: v.intervalo.inicio, fim: v.intervalo.fim };
    return o;
  };

  const replicarHorarioTodosDiasEst = (fonte: DiaSemana) => {
    setForm((f) => {
      const src = f.horarioSemanal[fonte];
      const next = { ...f.horarioSemanal };
      for (const d of DIAS_SEMANA) {
        next[d] = cloneHorarioDia(src);
      }
      return { ...f, horarioSemanal: next };
    });
    setDirty(true);
  };

  const setExcecaoDia = (dataStr: string, exc: ExcecaoDia | null) => {
    setForm((f) => {
      const next = { ...f.excecoesCalendario };
      if (exc) next[dataStr] = exc;
      else delete next[dataStr];
      return { ...f, excecoesCalendario: next };
    });
    setDirty(true);
  };

  const hoje = new Date();
  const anoMin = hoje.getFullYear();
  const dataMax = new Date(hoje);
  dataMax.setDate(dataMax.getDate() + 400);
  const anoMax = dataMax.getFullYear();
  const mesMax = dataMax.getMonth();

  const getDaysInMonth = (year: number, month: number) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const days = last.getDate();
    const startPad = first.getDay();
    return { days, startPad };
  };

  const listaStatusDropdownEst = listaStatusDropdown
    ? estabelecimentos.find((item) => item.id === listaStatusDropdown.estabelecimentoId) ?? null
    : null;
  const listaStatusDropdownModo =
    listaStatusDropdownEst && (listaStatusDropdownEst.publicacaoStatus ?? 'publicado') !== 'lixeira'
      ? getModoEstabelecimentoFromItem(listaStatusDropdownEst)
      : null;

  if (viewMode === 'list') {
    return (
      <AdminLayout>
        <div className="w-full min-w-0">
          <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
            <nav className="text-sm text-gray-500 mb-1">
              <Link to="/dashboard" className="hover:text-gray-700">
                Home
              </Link>
              {' > '}
              <Link to="/estabelecimentos" className="hover:text-gray-700">
                Estabelecimentos
              </Link>
            </nav>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Restaurantes e Comércios
              </h1>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setImportModalOpen(true)}
                  className="px-4 py-2 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Importar Estabelecimentos
                </button>
                <button
                  type="button"
                  onClick={openNew}
                  className="px-4 py-2 min-h-[44px] bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
                >
                  Novo Estabelecimento
                </button>
              </div>
            </div>
          </div>

          {adminIngestMisconfigured ? (
            <div className="mx-4 sm:mx-6 mt-3 rounded-lg border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm">
              <strong className="font-semibold">API não definida no build do admin.</strong> Publicar ou atualizar só
              persiste no navegador — o DynamoDB não recebe alterações até configurar{' '}
              <code className="rounded bg-white/90 px-1 py-0.5 text-xs">VITE_API_URL</code> (origem da API, ex.{' '}
              <code className="rounded bg-white/90 px-1 py-0.5 text-xs">https://api.naviexperience.com</code>, sem{' '}
              <code className="rounded bg-white/90 px-1 py-0.5 text-xs">/api</code>) ou <code className="rounded bg-white/90 px-1 py-0.5 text-xs">VITE_ADMIN_SYNC_URL</code>, e fazer novo deploy do painel.
            </div>
          ) : null}

          <div className="px-4 sm:px-6 py-6">
            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            {estabelecimentos.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <p>Nenhum estabelecimento cadastrado.</p>
                <button
                  type="button"
                  onClick={openNew}
                  className="mt-4 text-orange-600 font-medium hover:underline"
                >
                  Criar o primeiro
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
                <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="p-2 align-top w-[7.5rem]">
                      <label className="sr-only" htmlFor="est-lista-filtro-status">Filtrar por status</label>
                      <select
                        id="est-lista-filtro-status"
                        value={listaFilterStatus}
                        onChange={(e) => setListaFilterStatus(e.target.value as '' | ModoEstabelecimentoOpcao)}
                        className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md bg-white text-gray-800"
                      >
                        <option value="">Todos</option>
                        <option value="ativado">Ativado</option>
                        <option value="naviex">Destaque Naviex</option>
                        <option value="principal">Destaque Principal</option>
                        <option value="categoria">Destaque Categoria</option>
                        <option value="desativar">Desativado</option>
                      </select>
                    </th>
                    <th className="p-2 align-top min-w-[8rem]">
                      <label className="sr-only" htmlFor="est-lista-filtro-nome">Filtrar por nome</label>
                      <input
                        id="est-lista-filtro-nome"
                        type="search"
                        value={listaFilterNome}
                        onChange={(e) => setListaFilterNome(e.target.value)}
                        placeholder="Filtrar nome…"
                        className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md bg-white text-gray-800 placeholder:text-gray-400"
                      />
                    </th>
                    <th className="p-2 align-top min-w-[7rem]">
                      <label className="sr-only" htmlFor="est-lista-filtro-cat">Filtrar por categoria</label>
                      <select
                        id="est-lista-filtro-cat"
                        value={listaFilterCategoryId}
                        onChange={(e) => setListaFilterCategoryId(e.target.value)}
                        className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md bg-white text-gray-800"
                      >
                        <option value="">Todas</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </th>
                    <th className="p-2 align-top min-w-[7rem]">
                      <label className="sr-only" htmlFor="est-lista-filtro-cidade">Filtrar por cidade</label>
                      <select
                        id="est-lista-filtro-cidade"
                        value={listaFilterCityId}
                        onChange={(e) => setListaFilterCityId(e.target.value)}
                        className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md bg-white text-gray-800"
                      >
                        <option value="">Todas</option>
                        {cidades.map((c) => (
                          <option key={c.id} value={c.id}>{c.cidade}</option>
                        ))}
                      </select>
                    </th>
                    <th className="p-2 align-top w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem]">
                      <label className="sr-only" htmlFor="est-lista-filtro-pub">Filtrar por publicação</label>
                      <select
                        id="est-lista-filtro-pub"
                        value={listaFilterPublicacao}
                        onChange={(e) =>
                          setListaFilterPublicacao(e.target.value as '' | 'rascunho' | 'publicado' | 'lixeira')
                        }
                        className="w-full max-w-full py-1.5 px-1.5 text-xs border border-gray-300 rounded-md bg-white text-gray-800"
                      >
                        <option value="">Todas</option>
                        <option value="publicado">Publicado</option>
                        <option value="rascunho">Rascunho</option>
                        <option value="lixeira">Lixeira</option>
                      </select>
                    </th>
                    <th className="p-2 align-top text-right w-[4.5rem]">
                      <button
                        type="button"
                        onClick={limparFiltrosListaEstabelecimentos}
                        className="inline-flex items-center justify-center p-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-white hover:border-orange-400 hover:text-orange-600 text-xs font-medium"
                        title="Limpar filtros"
                        aria-label="Limpar todos os filtros da tabela"
                      >
                        Limpar
                      </button>
                    </th>
                  </tr>
                  <tr className="bg-orange-500 text-white">
                    <th className="text-left py-3 px-4 font-semibold">Status</th>
                    <th className="text-left py-3 px-4 font-semibold">Nome</th>
                    <th className="text-left py-3 px-4 font-semibold">Categoria</th>
                    <th className="text-left py-3 px-4 font-semibold">Cidade</th>
                    <th className="text-left py-3 px-2 font-semibold w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem] whitespace-nowrap">Publicação</th>
                    <th className="text-right py-3 px-4 font-semibold min-w-[6.5rem] w-[6.5rem]">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {estabelecimentosListaFiltrada.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-10 px-4 text-center text-gray-500 text-sm">
                        Nenhum estabelecimento corresponde aos filtros.
                        <button
                          type="button"
                          onClick={limparFiltrosListaEstabelecimentos}
                          className="block mx-auto mt-3 text-orange-600 font-medium hover:underline text-sm"
                        >
                          Limpar filtros
                        </button>
                      </td>
                    </tr>
                  ) : (
                  estabelecimentosListaFiltrada.map((est, idx) => {
                    const cidadeRow = est.cityId ? cidades.find((c) => c.id === est.cityId) : undefined;
                    const cidadeLabel =
                      cidadeRow != null
                        ? `${cidadeRow.cidade}${cidadeRow.estadoProvincia ? ` — ${cidadeRow.estadoProvincia}` : ''}`
                        : '—';
                    const modoLista =
                      (est.publicacaoStatus ?? 'publicado') !== 'lixeira'
                        ? getModoEstabelecimentoFromItem(est)
                        : null;
                    return (
                    <tr key={est.id} className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}>
                      <td className="py-3 px-4">
                        {modoLista ? (
                          <div
                            className="relative inline-block"
                            data-est-lista-status-toggle="true"
                            data-est-lista-status-anchor={est.id}
                          >
                            <button
                              type="button"
                              onClick={(event) => {
                                const rect = event.currentTarget.getBoundingClientRect();
                                setListaStatusDropdown((current) =>
                                  current?.estabelecimentoId === est.id
                                    ? null
                                    : { estabelecimentoId: est.id, top: rect.bottom + 4, left: rect.left }
                                );
                              }}
                              className={statusModoEstListaButtonClass(modoLista)}
                              aria-haspopup="listbox"
                              aria-expanded={listaStatusDropdown?.estabelecimentoId === est.id}
                              title="Alterar status"
                            >
                              <span className="inline-flex items-center gap-1.5 min-w-0">
                                <span
                                  className={`relative flex h-2 w-2 shrink-0 rounded-full ${statusModoEstDotClass(modoLista)}`}
                                />
                                <span className="truncate text-left">
                                  {EST_MODO_OPTIONS.find((o) => o.id === modoLista)?.label ?? modoLista}
                                </span>
                              </span>
                              <svg
                                className={`w-3.5 h-3.5 shrink-0 transition-transform ${
                                  listaStatusDropdown?.estabelecimentoId === est.id ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">{est.nome}</td>
                      <td className="py-3 px-4 text-gray-600">
                        {est.categoryId ? categories.find((c) => c.id === est.categoryId)?.name ?? '—' : '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-600">{cidadeLabel}</td>
                      <td className="py-3 px-2 w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem] whitespace-nowrap">
                        {(est.publicacaoStatus ?? 'publicado') === 'rascunho' ? (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-900 border border-amber-200">
                            Rascunho
                          </span>
                        ) : est.publicacaoStatus === 'lixeira' ? (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-800 border border-slate-300">
                            Lixeira
                          </span>
                        ) : (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Publicado
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 min-w-[6.5rem] w-[6.5rem] align-middle">
                        <div className="flex flex-row flex-nowrap items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openEdit(est.id)}
                            className="inline-flex shrink-0 items-center justify-center p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-orange-600 hover:border-orange-300 transition-colors"
                            title="Editar"
                            aria-label={`Editar ${est.nome}`}
                          >
                            <IconEstListEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(est)}
                            className="inline-flex shrink-0 items-center justify-center p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors"
                            title={est.publicacaoStatus === 'lixeira' ? 'Apagar permanentemente' : 'Mover para a lixeira'}
                            aria-label={
                              est.publicacaoStatus === 'lixeira'
                                ? `Apagar permanentemente ${est.nome}`
                                : `Mover para a lixeira ${est.nome}`
                            }
                          >
                            <IconEstListTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
              </div>
            )}
            </div>
          </div>
        </div>

        {(importModalOpen || categoryModalOpen || subcategoryModalOpen || (categoriasManagerOpen && !reassignModal) || reassignModal) && (
          <>
            {importModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4" onClick={() => setImportModalOpen(false)}>
              <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-4 p-6 border-b border-gray-200">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">Importar Estabelecimentos</h3>
                    <p className="text-sm text-gray-600">Busque no Google Places e preencha o formulário.</p>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                      <select
                        value={importCityId}
                        onChange={(e) => setImportCityId(e.target.value)}
                        className="w-full py-2 px-3 rounded-lg border border-gray-300"
                      >
                        <option value="">Selecione a cidade</option>
                        {cidades.map((c) => (
                          <option key={c.id} value={c.id}>{c.cidade}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                      <select
                        value={importType}
                        onChange={(e) => setImportType(e.target.value)}
                        className="w-full py-2 px-3 rounded-lg border border-gray-300"
                      >
                        <option value="restaurant">Alimentação</option>
                        <option value="store">Comércio</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void fetchImportPlaces()}
                    disabled={importLoading || !importCityId}
                    className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importLoading ? 'Buscando...' : 'Buscar no Google Places'}
                  </button>
                  {importResults.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <p className="text-sm font-medium text-gray-700 p-3 bg-gray-50 border-b">Resultados ({importResults.length}) — clique para preencher o formulário</p>
                      <ul className="max-h-[280px] overflow-y-auto divide-y divide-gray-100">
                        {importResults.map((place, i) => (
                          <li
                            key={place.googlePlaceId ?? i}
                            className="flex items-center justify-between p-3 hover:bg-orange-50 cursor-pointer"
                            onClick={() => applyImportToForm(place)}
                          >
                            <div>
                              <p className="font-medium text-gray-900">{place.nome}</p>
                              {place.formattedAddress && (
                                <p className="text-xs text-gray-500 truncate max-w-md">{place.formattedAddress}</p>
                              )}
                              {place.rating != null && (
                                <p className="text-xs text-amber-600 mt-0.5">★ {place.rating.toFixed(1)}{place.totalRatings != null ? ` (${place.totalRatings} avaliações)` : ''}</p>
                              )}
                            </div>
                            <span className="text-orange-600 text-sm font-medium">Usar</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-gray-200 flex justify-end">
                  <button type="button" onClick={() => setImportModalOpen(false)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                    Fechar
                  </button>
                </div>
              </div>
            </div>
            )}
            {categoryModalOpen && (
              <div className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4" onClick={() => setCategoryModalOpen(false)}>
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Nova categoria</h3>
                      <p className="text-sm text-gray-600">Cadastre uma nova categoria para estabelecimentos.</p>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    placeholder="Nome da categoria"
                  />
                  <div className="flex gap-3 justify-end mt-4">
                    <button type="button" onClick={() => setCategoryModalOpen(false)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                      Cancelar
                    </button>
                    <button type="button" onClick={() => void saveNewCategory()} className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600">
                      Salvar
                    </button>
                  </div>
                </div>
              </div>
            )}
            {subcategoryModalOpen && (
              <div className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4" onClick={() => setSubcategoryModalOpen(false)}>
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Nova subcategoria</h3>
                      <p className="text-sm text-gray-600">Associe a subcategoria a uma categoria.</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <select
                      value={newSubcategoryCategoryId}
                      onChange={(e) => setNewSubcategoryCategoryId(e.target.value)}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    >
                      <option value="">Selecione a categoria</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={newSubcategoryName}
                      onChange={(e) => setNewSubcategoryName(e.target.value)}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300"
                      placeholder="Nome da subcategoria"
                    />
                  </div>
                  <div className="flex gap-3 justify-end mt-4">
                    <button type="button" onClick={() => setSubcategoryModalOpen(false)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                      Cancelar
                    </button>
                    <button type="button" onClick={() => void saveNewSubcategory()} className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600">
                      Salvar
                    </button>
                  </div>
                </div>
              </div>
            )}
            {categoriasManagerOpen && !reassignModal && (
              <div className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4" onClick={() => setCategoriasManagerOpen(false)}>
                <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Gerenciar categorias</h3>
                      <p className="text-sm text-gray-600">Crie, exclua ou gerencie categorias e subcategorias.</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => { setCategoriasManagerOpen(false); setCategoryModalOpen(true); }}
                      className="px-3 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
                    >
                      Nova categoria
                    </button>
                    <button
                      type="button"
                      onClick={() => { setCategoriasManagerOpen(false); setSubcategoryModalOpen(true); }}
                      className="px-3 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
                    >
                      Nova subcategoria
                    </button>
                  </div>
                  <div className="space-y-4">
                    {categories.map((cat) => (
                      <div key={cat.id} className="border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{cat.name}</span>
                          <button type="button" onClick={() => void handleDeleteCategory(cat)} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-lg">
                            Excluir
                          </button>
                        </div>
                        <div className="space-y-1 pl-2 border-l-2 border-gray-200">
                          {getEstabelecimentoSubcategoriesByCategory(cat.id).map((sub) => (
                            <div key={sub.id} className="flex items-center justify-between py-1">
                              <span className="text-sm text-gray-600">{sub.name}</span>
                              <button type="button" onClick={() => void handleDeleteSubcategory(sub)} className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded">
                                Excluir
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end mt-4">
                    <button type="button" onClick={() => setCategoriasManagerOpen(false)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            )}
            {reassignModal && (
              <div className="fixed inset-0 bg-black/50 z-[350] flex items-center justify-center p-4" onClick={() => setReassignModal(null)}>
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Reatribuir antes de excluir</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {`${reassignModal.establishmentsCount} estabelecimento(s) `}
                        em &quot;{reassignModal.name}&quot;. Escolha uma nova categoria e subcategoria para eles:
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Nova categoria</label>
                      <select
                        value={reassignNewCategoryId}
                        onChange={(e) => { setReassignNewCategoryId(e.target.value); setReassignNewSubcategoryId(''); }}
                        className="w-full py-2 px-3 rounded-lg border border-gray-300"
                      >
                        <option value="">Selecione</option>
                        {categories.filter((c) => reassignModal.type !== 'category' || c.id !== reassignModal.id).map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      {reassignModal.type === 'category' && categories.filter((c) => c.id !== reassignModal.id).length === 0 && (
                        <p className="text-xs text-amber-700 mt-1">Cadastre outra categoria antes de excluir esta.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Nova subcategoria</label>
                      <select
                        value={reassignNewSubcategoryId}
                        onChange={(e) => setReassignNewSubcategoryId(e.target.value)}
                        className="w-full py-2 px-3 rounded-lg border border-gray-300"
                      >
                        <option value="">Selecione</option>
                        {reassignNewCategoryId &&
                          getEstabelecimentoSubcategoriesByCategory(reassignNewCategoryId)
                            .filter((s) => reassignModal.type !== 'subcategory' || s.id !== reassignModal.id)
                            .map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 justify-end mt-4">
                    <button type="button" onClick={() => setReassignModal(null)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => void confirmReassignAndDelete()}
                      disabled={!reassignNewCategoryId || !reassignNewSubcategoryId}
                      className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Reatribuir e excluir
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {listaStatusDropdown &&
          listaStatusDropdownEst &&
          listaStatusDropdownModo &&
          typeof document !== 'undefined' &&
          document.body &&
          createPortal(
            <div
              id="estabelecimentos-lista-status-dropdown-portal"
              role="listbox"
              className={EST_MODO_LISTA_DROPDOWN_CLASS}
              style={{ top: listaStatusDropdown.top, left: listaStatusDropdown.left }}
            >
              {EST_MODO_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={opt.id === listaStatusDropdownModo}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const id =
                      listaStatusDropdownRef.current?.estabelecimentoId ??
                      listaStatusDropdown.estabelecimentoId;
                    applyModoStatusFromLista(id, opt.id);
                  }}
                  className={`flex w-full items-center gap-2 py-2 text-left text-xs font-medium whitespace-nowrap ${
                    opt.id === listaStatusDropdownModo
                      ? 'bg-orange-50 text-orange-900'
                      : 'bg-white text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <span className={`rounded-full h-2 w-2 shrink-0 ${statusModoEstDotClass(opt.id)}`} />
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>,
            document.body
          )}
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="w-full min-w-0">
        <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">
            <Link to="/dashboard" className="hover:text-gray-700">
              Home
            </Link>
            {' > '}
            <Link to="/estabelecimentos" className="hover:text-gray-700">
              Estabelecimentos
            </Link>
            {viewMode === 'form' && (
              <>
                {' '}
                {' > '}
                <span className="text-gray-800">
                  {form.nome.trim() || (editingId ? 'Editar estabelecimento' : 'Novo estabelecimento')}
                </span>
              </>
            )}
          </nav>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {form.nome.trim() || (editingId ? 'Editar estabelecimento' : 'Novo estabelecimento')}
              </h1>
              <div className="relative" ref={statusComboRef}>
                <button
                  type="button"
                  onClick={() => setStatusComboContext((c) => (c === 'header' ? null : 'header'))}
                  className={statusModoEstButtonClass(modoEstabelecimentoAtual)}
                  aria-haspopup="listbox"
                  aria-expanded={statusComboContext === 'header'}
                >
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <span className={`relative flex h-2 w-2 shrink-0 rounded-full ${statusModoEstDotClass(modoEstabelecimentoAtual)}`} />
                    <span className="truncate text-left">
                      {EST_MODO_OPTIONS.find((o) => o.id === modoEstabelecimentoAtual)?.label ?? 'Status'}
                    </span>
                  </span>
                  <svg
                    className={`w-4 h-4 shrink-0 transition-transform ${statusComboContext === 'header' ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {statusComboContext === 'header' && (
                  <div
                    className="absolute top-full left-0 mt-1 py-1 rounded-lg border-2 border-gray-200 bg-white shadow-lg z-10 min-w-[15rem] max-w-[min(100vw-2rem,20rem)]"
                    role="listbox"
                  >
                    {EST_MODO_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        role="option"
                        aria-selected={opt.id === modoEstabelecimentoAtual}
                        onClick={() => {
                          setForm((f) => ({ ...f, ...buildUpdateModoEstabelecimento(opt.id) }));
                          setStatusComboContext(null);
                          setDirty(true);
                        }}
                        className={`w-full inline-flex items-center gap-2 px-4 py-2.5 text-left text-sm font-medium ${
                          opt.id === modoEstabelecimentoAtual ? 'bg-orange-50/80' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`rounded-full h-2 w-2 shrink-0 ${statusModoEstDotClass(opt.id)}`} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 items-center justify-end">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handlePublishSave()}
                disabled={publicacaoIssues.length > 0}
                title={
                  publicacaoIssues.length > 0
                    ? `Preencha: ${publicacaoIssues.join(', ')}`
                    : undefined
                }
                className={`px-4 py-2 min-h-[44px] rounded-lg font-medium whitespace-nowrap shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                  publicacaoIssues.length > 0
                    ? 'bg-gray-300 text-gray-600'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                {textoBotaoPrincipalForm}
              </button>
            </div>
          </div>
        </div>

        {adminIngestMisconfigured ? (
          <div className="mx-4 sm:mx-6 mt-3 rounded-lg border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm">
            <strong className="font-semibold">API não definida no build do admin.</strong> Publicar ou atualizar só
            persiste no navegador — o DynamoDB não recebe alterações até configurar{' '}
            <code className="rounded bg-white/90 px-1 py-0.5 text-xs">VITE_API_URL</code> (origem da API, ex.{' '}
            <code className="rounded bg-white/90 px-1 py-0.5 text-xs">https://api.naviexperience.com</code>, sem{' '}
            <code className="rounded bg-white/90 px-1 py-0.5 text-xs">/api</code>) ou <code className="rounded bg-white/90 px-1 py-0.5 text-xs">VITE_ADMIN_SYNC_URL</code>, e fazer novo deploy do painel.
          </div>
        ) : null}

        <div className="px-4 sm:px-6 py-6 w-full">
          <div className="flex gap-2 mb-6 w-full flex-wrap overflow-x-auto pb-2">
            {[
              { id: 'informacoes' as const, label: 'Informações Gerais' },
              { id: 'localizacao' as const, label: 'Localização' },
              { id: 'imagens' as const, label: 'Imagens' },
              { id: 'descontoParceria' as const, label: 'Desconto e Parceria' },
              { id: 'status' as const, label: 'Status Programado' },
              { id: 'funcionamento' as const, label: 'Horário de funcionamento' },
              { id: 'calendarioEventos' as const, label: 'Calendário e Eventos' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setFormTab(tab.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 ${formTab === tab.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-xl shadow border border-gray-200 p-4 sm:p-6 space-y-6">
            {formTab === 'informacoes' && (
            <>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
              <div className="lg:col-span-9 space-y-6 min-w-0">
                <div>
                  <div className="flex flex-wrap items-stretch gap-2">
                    <div className="min-w-0 flex-1 flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                      <input
                        ref={nomeInputRef}
                        type="text"
                        value={form.nome}
                        onChange={(e) => {
                          setForm((f) => ({ ...f, nome: e.target.value }));
                          setDirty(true);
                        }}
                        onPaste={(e) => {
                          const pasted = (e.clipboardData?.getData?.('text') ?? '').trim();
                          if (looksLikeMapsLink(pasted) || /^-?\d{1,3}\.\d+/.test(pasted)) {
                            e.preventDefault();
                            setForm((f) => ({ ...f, nome: pasted }));
                            setDirty(true);
                            void parseGoogleMapsLink(pasted).then((coords) => {
                              if (coords) applyMapsLink(pasted, coords);
                            });
                          }
                        }}
                        className="w-full h-10 px-3 box-border border border-gray-300 rounded-lg text-sm"
                        placeholder="Digite o nome ou endereço — escolha na lista do Google"
                        autoComplete="off"
                      />
                    </div>
                    <div className="w-full shrink-0 font-mono text-sm sm:w-auto sm:min-w-[18rem] flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-1 truncate font-sans" title={isGoogleImport ? 'CNPJ (opcional)' : 'CNPJ'}>
                        CNPJ {!isGoogleImport ? '*' : ''}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.cnpj}
                        onChange={(e) => {
                          const formatted = formatCnpj(e.target.value);
                          setForm((f) => ({ ...f, cnpj: formatted }));
                          setDirty(true);
                        }}
                        className="box-border w-full h-10 px-3 border border-gray-300 rounded-lg font-mono text-sm tabular-nums tracking-normal"
                        placeholder="00.000.000/0001-00"
                        maxLength={18}
                        aria-label={isGoogleImport ? 'CNPJ (opcional)' : 'CNPJ'}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo</label>
                  <input
                    type="text"
                    value={form.subtitulo}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, subtitulo: e.target.value }));
                      setDirty(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Linha curta opcional (ex.: especialidade ou bairro)"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resumo *</label>
                  <textarea
                    value={form.resumo}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, resumo: e.target.value }));
                      setDirty(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[88px] resize-y"
                    placeholder="Resumo curto para listagens e vitrine (obrigatório)"
                    rows={3}
                  />
                </div>

                <div>
                  <div className="block text-sm font-medium text-gray-700 mb-1">Descrição *</div>
                  <EstabelecimentoDescricaoEditor
                    key={editingId ? `desc-${editingId}` : 'desc-novo'}
                    id="est-form-descricao"
                    value={form.descricao}
                    onChange={(html) => {
                      setForm((f) => ({ ...f, descricao: html }));
                      setDirty(true);
                    }}
                    placeholder="Descreva o estabelecimento..."
                  />
                </div>
              </div>

              <aside className="lg:col-span-3 space-y-4 min-w-0">
                <InformacoesSidebarMeta title="Categoria *">
                  <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setCategoryModalOpen(true)}
                      className="text-xs text-orange-600 hover:underline"
                    >
                      Nova categoria
                    </button>
                    <button
                      type="button"
                      onClick={() => setCategoriasManagerOpen(true)}
                      className="text-xs text-gray-600 hover:underline"
                    >
                      Gerenciar
                    </button>
                  </div>
                  <select
                    value={form.categoryId}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, categoryId: e.target.value, subcategoryIds: [] }));
                      setDirty(true);
                    }}
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value="">Selecione</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </InformacoesSidebarMeta>

                <InformacoesSidebarMeta title="Subcategorias *">
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={() => setSubcategoryModalOpen(true)}
                      className="text-xs text-orange-600 hover:underline"
                    >
                      Nova subcategoria
                    </button>
                  </div>
                  {subcategoriesForForm.length === 0 && form.categoryId ? (
                    <p className="text-xs text-gray-500">Nenhuma subcategoria para esta categoria</p>
                  ) : !form.categoryId ? (
                    <p className="text-xs text-gray-500">Selecione uma categoria.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 rounded-lg border border-gray-200 bg-gray-50 p-2">
                      {subcategoriesForForm.map((s) => (
                        <label
                          key={s.id}
                          className="inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 text-xs hover:bg-orange-50 has-[:checked]:border-orange-400 has-[:checked]:bg-orange-50/60"
                        >
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 shrink-0"
                            checked={form.subcategoryIds.includes(s.id)}
                            onChange={() => {
                              setForm((f) => {
                                const has = f.subcategoryIds.includes(s.id);
                                const next = has ? f.subcategoryIds.filter((id) => id !== s.id) : [...f.subcategoryIds, s.id];
                                return { ...f, subcategoryIds: next };
                              });
                              setDirty(true);
                            }}
                          />
                          <span className="leading-tight">{s.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </InformacoesSidebarMeta>

                <InformacoesSidebarMeta title="Responsáveis">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.responsaveis.map((r) => (
                      <span
                        key={r.userId}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-800 rounded text-xs"
                      >
                        {r.name}
                        {r.phone && (
                          <a
                            href={`https://wa.me/55${r.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:underline"
                            title="WhatsApp"
                          >
                            WA
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => removeResponsavel(r.userId)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={responsavelQuery}
                      onChange={(e) => {
                        setResponsavelQuery(e.target.value);
                        setResponsavelOpen(true);
                      }}
                      onFocus={() => setResponsavelOpen(true)}
                      placeholder="Buscar usuário…"
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                    {responsavelOpen && (
                      <div className="absolute z-[280] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredUsers.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500 text-sm">Nenhum usuário encontrado</div>
                        ) : (
                          filteredUsers.map((u) => (
                            <button
                              key={u.userId}
                              type="button"
                              onClick={() => addResponsavel(u)}
                              className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm"
                            >
                              {u.name} ({u.email})
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </InformacoesSidebarMeta>

                <InformacoesSidebarMeta title="Tags">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={tagInputEst}
                      onChange={(e) => setTagInputEst(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addNovaTagEstabelecimento(tagInputEst);
                        }
                      }}
                      placeholder="Nova tag…"
                      className="flex-1 min-w-[6rem] py-1.5 px-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => addNovaTagEstabelecimento(tagInputEst)}
                      className="px-2.5 py-1.5 rounded-lg border border-orange-500 text-orange-600 text-xs font-medium hover:bg-orange-50 shrink-0"
                    >
                      Adicionar
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Lista própria de Estabelecimentos; toque para marcar ou desmarcar.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {tagsDisponiveisEstabelecimento.map((tag) => {
                      const selected = (form.tags ?? []).includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTagEstabelecimento(tag)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${selected ? 'bg-orange-100 border-orange-400 text-orange-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </InformacoesSidebarMeta>
              </aside>
            </div>
            </>
            )}

            {formTab === 'status' && (
              <StatusProgramadoAgendaPanel
                minDateYmd={hojeCalendarioSelecao}
                periodoDe={form.periodoDe}
                periodoAte={form.periodoAte}
                onPeriodoChange={(de, ate) => {
                  setForm((f) => ({ ...f, periodoDe: de, periodoAte: ate }));
                  setDirty(true);
                }}
                periodosStatus={form.periodosStatus ?? []}
                statusProgramadoHistorico={form.statusProgramadoHistorico ?? []}
                diaAgendaStatus={diaAgendaStatusEst}
                onRemovePeriodo={removerPeriodoStatus}
                onAddPeriodo={(tipo) =>
                  adicionarPeriodoStatus(tipo as 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria')
                }
                labelTipoProgramado={labelTipoProgramadoEstabelecimento}
                tipoAtivo="ativado"
                tipoDesativado="desativado"
              />
            )}

            {formTab === 'funcionamento' && (
          <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full">
            <h2 className="text-lg font-semibold text-gray-800 shrink-0 mb-6">Horário de funcionamento</h2>

            <h3 className="text-base font-semibold text-gray-800 mb-2">Dias da semana</h3>
            <p className="text-sm text-gray-600 mb-4">Defina os horários por dia da semana. Marque "Fechado" para dias sem expediente. Use "Fechar para almoço" para dias com intervalo.</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 mb-4 p-3 rounded-xl bg-white border border-orange-200 shadow-sm max-w-2xl">
              <span className="text-sm font-medium text-gray-800">Replicar horário</span>
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="dia-fonte-horario-est" className="text-sm text-gray-600">
                  Usar como modelo:
                </label>
                <select
                  id="dia-fonte-horario-est"
                  value={diaFonteHorarioEst}
                  onChange={(e) => setDiaFonteHorarioEst(e.target.value as DiaSemana)}
                  className="py-1.5 px-3 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 min-w-[11rem]"
                >
                  {(DIAS_SEMANA as DiaSemana[]).map((key) => (
                    <option key={key} value={key}>
                      {DIAS_LABEL[key]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => replicarHorarioTodosDiasEst(diaFonteHorarioEst)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm"
                >
                  Aplicar a todos os dias
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {(DIAS_SEMANA as DiaSemana[]).map((dia) => {
                const h = form.horarioSemanal[dia];
                const temIntervalo = h !== null && !!h.intervalo;
                return (
                  <div key={dia} className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 py-3 px-3 rounded-lg bg-white border border-gray-200">
                    <span className="w-full sm:w-24 text-sm font-medium text-gray-700 shrink-0">{DIAS_LABEL[dia]}</span>
                    <label className="flex items-center gap-2 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={h === null}
                        onChange={(e) =>
                          updateHorarioDia(dia, e.target.checked ? null : { abertura: '08:00', fechamento: '18:00' })
                        }
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                      />
                      <span className="text-sm text-gray-600">Fechado</span>
                    </label>
                    {h !== null && (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={temIntervalo}
                            onChange={(e) =>
                              updateHorarioDia(dia, e.target.checked
                                ? { ...h, intervalo: { inicio: '12:00', fim: '13:00' } }
                                : (() => { const { intervalo: _, ...rest } = h; return rest; })()
                              )
                            }
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                          />
                          <span className="text-sm text-gray-600">Fechar para almoço</span>
                        </label>
                        {!temIntervalo ? (
                          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <TimeSelect
                              value={h.abertura}
                              onChange={(v) => updateHorarioDia(dia, { ...h, abertura: v })}
                            />
                            <span className="text-gray-500">—</span>
                            <TimeSelect
                              value={h.fechamento}
                              onChange={(v) => updateHorarioDia(dia, { ...h, fechamento: v })}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 w-full sm:w-auto">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-gray-500 shrink-0">Manhã</span>
                              <TimeSelect value={h.abertura} onChange={(v) => updateHorarioDia(dia, { ...h, abertura: v })} />
                              <span className="text-gray-500">—</span>
                              <TimeSelect value={h.intervalo!.inicio} onChange={(v) => updateHorarioDia(dia, { ...h, intervalo: { ...h.intervalo!, inicio: v } })} />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-gray-500 shrink-0">Tarde</span>
                              <TimeSelect value={h.intervalo!.fim} onChange={(v) => updateHorarioDia(dia, { ...h, intervalo: { ...h.intervalo!, fim: v } })} />
                              <span className="text-gray-500">—</span>
                              <TimeSelect value={h.fechamento} onChange={(v) => updateHorarioDia(dia, { ...h, fechamento: v })} />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
            )}

            {formTab === 'calendarioEventos' && (
          <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full min-w-0">
            <h2 className="text-lg font-semibold text-gray-800 shrink-0 mb-2">Calendário e Eventos</h2>
            <p className="text-sm text-gray-600 mb-6">
              Exceções por data (feriados, horários especiais) e dias com eventos associados a este estabelecimento.
            </p>
            <div className="p-6 border border-gray-200 rounded-2xl bg-white shadow-sm w-full">
                <div className="flex items-center justify-between mb-6">
                  <button
                    type="button"
                    onClick={() => {
                      if (calMes === 0) {
                        setCalMes(11);
                        setCalAno((a) => a - 1);
                      } else setCalMes((m) => m - 1);
                    }}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-700 transition-colors"
                    aria-label="Mês anterior"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {new Date(calAno, calMes).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      if (calMes === 11) {
                        setCalMes(0);
                        setCalAno((a) => a + 1);
                      } else setCalMes((m) => m + 1);
                    }}
                    disabled={calAno > anoMax || (calAno === anoMax && calMes > mesMax)}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    aria-label="Próximo mês"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18L15 12L9 6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-2 mb-2 text-center text-xs font-semibold text-gray-600 py-2 w-full">
                  {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map((l,i)=><span key={i}>{l}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-2 text-sm w-full">
                  {(() => {
                    const { days, startPad } = getDaysInMonth(calAno, calMes);
                    const cells: JSX.Element[] = [];
                    const hojeStart = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();
                    for (let i = 0; i < startPad; i++) {
                      cells.push(<span key={`e-${i}`} />);
                    }
                    const eventosDoEst = editingId ? getEventsByEstabelecimento(editingId) : [];
                    for (let d = 1; d <= days; d++) {
                      const dataStr = `${calAno}-${String(calMes + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      const exc = form.excecoesCalendario[dataStr];
                      const dayTime = new Date(calAno, calMes, d).getTime();
                      const isPast = dayTime < hojeStart;
                      const eventosNaData = eventosDoEst.filter((ev) => dataStr >= ev.startDate && dataStr <= ev.endDate);
                      const hasEvento = eventosNaData.length > 0;
                      const diaSemana = DIA_SEMANA_FROM_JS[new Date(calAno, calMes, d).getDay()];
                      const isFechadoSemanal = form.horarioSemanal?.[diaSemana] === null;
                      const horarioAbertura = exc?.tipo === 'horario' ? (exc.abertura ?? '08:00') : null;
                      const horarioFechamento = exc?.tipo === 'horario' ? (exc.fechamento ?? '18:00') : null;
                      const horarioPadraoDia = !exc && !isFechadoSemanal && form.horarioSemanal?.[diaSemana]
                        ? form.horarioSemanal[diaSemana]
                        : null;
                      const horarioAberto = horarioAbertura && horarioFechamento
                        ? `${horarioAbertura}-${horarioFechamento}`
                        : horarioPadraoDia
                          ? formatarHorarioDia(horarioPadraoDia)
                          : null;
                      cells.push(
                        <button
                          key={d}
                          type="button"
                          disabled={isPast}
                          onClick={() => setDiaEditModal({ dataStr, exc: exc ?? null })}
                          className={`min-h-[64px] sm:min-h-[72px] rounded-lg font-medium transition-all flex flex-col items-start justify-start text-left p-2 w-full ${
                            isPast
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : hasEvento
                                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                : exc
                                  ? exc.tipo === 'fechado'
                                    ? 'bg-gray-800 text-white hover:bg-gray-700'
                                    : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                : isFechadoSemanal
                                  ? 'bg-gray-800 text-white hover:bg-gray-700'
                                  : 'bg-gray-50 text-gray-700 hover:bg-orange-100 hover:text-orange-800'
                          }`}
                          title={hasEvento ? `${eventosNaData.map(e=>e.title).join(', ')} - Clique para editar` : exc ? (exc.tipo === 'fechado' ? 'Fechado - Clique para editar' : `${exc.abertura}-${exc.fechamento} - Clique para editar`) : isFechadoSemanal ? 'Fechado (horário semanal) - Clique para editar' : horarioAberto ? `${horarioAberto} - Clique para editar` : 'Clique para definir exceção'}
                        >
                          <span className="leading-tight text-base font-bold">{d}</span>
                          {!isPast && horarioAberto && (
                            <span className="text-[10px] sm:text-xs mt-1 font-normal opacity-90 line-clamp-2">
                              {horarioAberto}
                            </span>
                          )}
                          {!isPast && hasEvento && (
                            <span className="text-[10px] sm:text-xs mt-0.5 font-normal truncate w-full" title={eventosNaData.map(e=>e.title).join(', ')}>
                              {eventosNaData[0].title}
                            </span>
                          )}
                        </button>
                      );
                    }
                    return cells;
                  })()}
                </div>
              </div>
          </section>
            )}

            {formTab === 'localizacao' && (
          <section className="w-full min-w-0 space-y-8">
            <h2 className="text-lg font-semibold text-gray-800">Localização</h2>
            <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="rounded-xl border border-gray-200 p-5 space-y-4">
                <h3 className="text-base font-semibold text-gray-800 mb-2">Endereço</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Só podem ser cadastrados estabelecimentos em cidades já registradas. Na aba Informações gerais ou aqui, ao escolher uma sugestão do Google, o nome, o link do mapa e a cidade passam a refletir o mesmo lugar. A cidade só é preenchida automaticamente se o município corresponder a uma cidade cadastrada (por nome).
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade *</label>
                  <select
                    value={form.cityId}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, cityId: e.target.value }));
                      setDirty(true);
                    }}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="">Selecione a cidade cadastrada</option>
                    {cidades.map((c) => (
                      <option key={c.id} value={c.id}>{c.cidade} — {c.estadoProvincia}</option>
                    ))}
                  </select>
                  {cidades.length === 0 && (
                    <p className="text-xs text-amber-700 mt-1">
                      Nenhuma cidade cadastrada. Cadastre em <Link to="/cidades" className="text-orange-600 hover:underline">Cidades</Link> primeiro.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local no mapa *</label>
                  <input
                    ref={enderecoInputRef}
                    type="text"
                    value={form.endereco ?? ''}
                    onChange={(e) => {
                      const v = e.target.value ?? '';
                      setForm((f) => ({ ...f, endereco: v }));
                      setDirty(true);
                    }}
                    onPaste={(e) => {
                      const pasted = (e.clipboardData?.getData?.('text') ?? '').trim();
                      if (looksLikeMapsLink(pasted) || /^-?\d{1,3}\.\d+/.test(pasted)) {
                        e.preventDefault();
                        setForm((f) => ({ ...f, endereco: pasted }));
                        setDirty(true);
                        parseGoogleMapsLink(pasted).then((coords) => {
                          if (coords) applyMapsLink(pasted, coords);
                        });
                      }
                    }}
                    placeholder="Digite o nome do estabelecimento ou endereço — escolha na lista do Google"
                    className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400"
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Sugestões do Google Places (Brasil). Também pode colar link do Maps, coordenadas ou usar &quot;Compartilhar&quot; → &quot;Copiar link&quot;.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-5 w-full">
                <p className="text-sm font-medium text-gray-700 mb-2">Localização no mapa</p>
                {!googleMapsApiKey && (
                  <p className="mb-2 text-xs text-amber-700">Configure `VITE_GOOGLE_MAPS_API_KEY` para habilitar autocomplete e mapa.</p>
                )}
                {mapsError && (
                  <p className="mb-2 text-xs text-red-600">{mapsError}</p>
                )}
                <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden relative min-h-[520px] w-full">
                  {mapsError ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-sm px-4 py-6 text-center">
                      <p className="text-red-600 font-medium mb-2">Erro ao carregar o Google Maps</p>
                      <p className="text-gray-600 text-xs max-w-md">{mapsError}</p>
                      <p className="text-gray-500 text-xs mt-3">Verifique a chave da API no Google Cloud Console.</p>
                    </div>
                  ) : mapsReady && locCoords ? (
                    <div ref={mapContainerRef} className="absolute inset-0 w-full h-full" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm px-3 text-center">
                      Cole o link do Google Maps acima para visualizar a localização no mapa.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
            )}

            {formTab === 'imagens' && (
              <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Imagens</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Até {MAX_FOTOS_ESTABELECIMENTO} fotos. O ficheiro é enviado para o armazenamento na nuvem (S3); aqui ficam só as referências. A{' '}
                  <strong className="font-semibold text-gray-700">primeira imagem</strong> é a de capa nos cards. Defina o endereço ou link do Maps na aba{' '}
                  <strong className="font-semibold text-gray-700">Localização</strong> antes de importar fotos do Google.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => void handleImportarFotos()}
                    disabled={importFotosLoading || imagemUploadBusy || (form.fotos?.length ?? 0) >= MAX_FOTOS_ESTABELECIMENTO}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importFotosLoading ? (
                      'Importando...'
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Importar fotos do Google
                      </>
                    )}
                  </button>
                  <span className="text-xs text-gray-500 self-center max-w-xl">
                    Usa o link do Maps na aba Localização ou pede para colar o URL. Com estabelecimento já guardado, as fotos importadas podem ser salvas automaticamente.
                  </span>
                </div>
                <div
                  className={`border-2 border-dashed border-gray-300 rounded-xl p-8 text-center bg-white transition-colors ${
                    imagemUploadBusy || (form.fotos?.length ?? 0) >= MAX_FOTOS_ESTABELECIMENTO
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:border-orange-400 cursor-pointer'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (imagemUploadBusy || (form.fotos?.length ?? 0) >= MAX_FOTOS_ESTABELECIMENTO) return;
                    e.currentTarget.classList.add('border-orange-400', 'bg-orange-50/50');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50/50');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50/50');
                    if (imagemUploadBusy || (form.fotos?.length ?? 0) >= MAX_FOTOS_ESTABELECIMENTO) return;
                    handleImageFiles(e.dataTransfer.files);
                  }}
                  onClick={() => {
                    if (imagemUploadBusy || (form.fotos?.length ?? 0) >= MAX_FOTOS_ESTABELECIMENTO) return;
                    document.getElementById('estabelecimento-imagens-input')?.click();
                  }}
                >
                  <input
                    id="estabelecimento-imagens-input"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    multiple
                    disabled={imagemUploadBusy || (form.fotos?.length ?? 0) >= MAX_FOTOS_ESTABELECIMENTO}
                    className="hidden"
                    onChange={(e) => {
                      handleImageFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-gray-700 font-medium">{imagemUploadBusy ? 'A enviar…' : 'Faça o upload'}</p>
                  <p className="text-sm text-gray-500 mt-1">PNG ou JPG — comprimidos antes do envio para o bucket S3 (categoria estabelecimentos)</p>
                </div>
                {form.fotos && form.fotos.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {form.fotos.map((src, i) => (
                      <div
                        key={`${src}-${i}`}
                        draggable={!imagemUploadBusy}
                        onDragStart={() => {
                          if (imagemUploadBusy) return;
                          setDraggingFotoIndex(i);
                          setDragOverFotoIndex(i);
                        }}
                        onDragOver={(e) => {
                          if (imagemUploadBusy || draggingFotoIndex === null) return;
                          e.preventDefault();
                          if (dragOverFotoIndex !== i) setDragOverFotoIndex(i);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (imagemUploadBusy) return;
                          handleFotoDrop(i);
                        }}
                        onDragEnd={resetFotoDragState}
                        className={`relative group rounded-lg overflow-hidden aspect-[4/3] bg-gray-100 ${
                          imagemUploadBusy ? '' : 'cursor-grab active:cursor-grabbing'
                        } ${
                          dragOverFotoIndex === i && draggingFotoIndex !== i
                            ? 'ring-2 ring-orange-300 ring-offset-2 ring-offset-gray-50'
                            : ''
                        }`}
                      >
                        <div
                          className={`relative h-full w-full ${
                            i === 0
                              ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-gray-50 border-2 border-orange-500'
                              : 'border border-gray-200'
                          }`}
                        >
                          <ImageWithMediaFallback
                            src={resolveMediaUrl(src, getApiBase())}
                            alt={`NaviExperience - ${form.nome || 'Estabelecimento'} - Foto ${i + 1}`}
                            title={`NaviExperience - ${form.nome || 'Estabelecimento'} - Foto ${i + 1}`}
                            draggable={false}
                            className="w-full h-full object-cover"
                          />
                          {i === 0 && (
                            <span className="absolute bottom-2 left-2 px-2.5 py-1 rounded-md text-sm font-bold bg-orange-500 text-white shadow-md tracking-tight">
                              principal
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setForm((f) => ({ ...f, fotos: (f.fotos ?? []).filter((_, j) => j !== i) }));
                            setDirty(true);
                          }}
                          className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-500 text-white text-sm hover:bg-red-600 z-10 shadow"
                          aria-label="Remover foto"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {formTab === 'descontoParceria' && (
              <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full space-y-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Desconto e Parceria</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Descreva o tipo de parceria, descontos para clientes Navi, condições de uso ou outras informações comerciais. Campos opcionais.
                </p>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
                  <div className="flex items-start gap-3">
                    <input
                      id="est-desconto-morador-ativo"
                      type="checkbox"
                      checked={form.descontoMoradorAtivo}
                      onChange={(e) => {
                        const ativo = e.target.checked;
                        setForm((f) => ({
                          ...f,
                          descontoMoradorAtivo: ativo,
                          ...(ativo
                            ? {}
                            : {
                                descontoMoradorModo: 'percent' as DescontoMoradorModo,
                                descontoMoradorValor: '',
                                descontoMoradorTexto: '',
                              }),
                        }));
                        setDirty(true);
                      }}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <div className="min-w-0 flex-1">
                      <label htmlFor="est-desconto-morador-ativo" className="text-sm font-medium text-gray-800 cursor-pointer">
                        Desconto morador
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Ative para registar desconto exclusivo para moradores (percentual, valor em reais ou descrição livre).
                      </p>
                    </div>
                  </div>
                  {form.descontoMoradorAtivo && (
                    <div className="pl-0 sm:pl-7 space-y-3 border-t border-gray-100 pt-3">
                      <fieldset className="space-y-2">
                        <legend className="text-xs font-medium text-gray-600 mb-1">Tipo de desconto</legend>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="est-desconto-morador-modo"
                              checked={form.descontoMoradorModo === 'percent'}
                              onChange={() => {
                                setForm((f) => ({ ...f, descontoMoradorModo: 'percent' }));
                                setDirty(true);
                              }}
                              className="text-orange-600 focus:ring-orange-500"
                            />
                            Percentual (%)
                          </label>
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="est-desconto-morador-modo"
                              checked={form.descontoMoradorModo === 'currency'}
                              onChange={() => {
                                setForm((f) => ({ ...f, descontoMoradorModo: 'currency' }));
                                setDirty(true);
                              }}
                              className="text-orange-600 focus:ring-orange-500"
                            />
                            Valor (R$)
                          </label>
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="est-desconto-morador-modo"
                              checked={form.descontoMoradorModo === 'text'}
                              onChange={() => {
                                setForm((f) => ({ ...f, descontoMoradorModo: 'text' }));
                                setDirty(true);
                              }}
                              className="text-orange-600 focus:ring-orange-500"
                            />
                            Descritivo
                          </label>
                        </div>
                      </fieldset>
                      {form.descontoMoradorModo === 'text' ? (
                        <div>
                          <label htmlFor="est-desconto-morador-texto" className="block text-sm font-medium text-gray-700 mb-1">
                            Descrição do desconto
                          </label>
                          <textarea
                            id="est-desconto-morador-texto"
                            value={form.descontoMoradorTexto}
                            onChange={(e) => {
                              setForm((f) => ({ ...f, descontoMoradorTexto: e.target.value }));
                              setDirty(true);
                            }}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y min-h-[88px]"
                            placeholder="Ex.: desconto progressivo conforme comprovante de residência…"
                          />
                          <p className="text-xs text-gray-500 mt-1">Obrigatório ao publicar, se esta opção estiver ativa.</p>
                        </div>
                      ) : (
                        <div>
                          <label htmlFor="est-desconto-morador-valor" className="block text-sm font-medium text-gray-700 mb-1">
                            {form.descontoMoradorModo === 'percent' ? 'Percentual' : 'Valor em reais'}
                          </label>
                          <input
                            id="est-desconto-morador-valor"
                            type="text"
                            inputMode="decimal"
                            value={form.descontoMoradorValor}
                            onChange={(e) => {
                              setForm((f) => ({ ...f, descontoMoradorValor: e.target.value }));
                              setDirty(true);
                            }}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder={form.descontoMoradorModo === 'percent' ? 'Ex.: 15' : 'Ex.: 19,90'}
                          />
                          <p className="text-xs text-gray-500 mt-1">Valor obrigatório ao publicar (número maior que zero).</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <label htmlFor="est-desconto-parceria-texto" className="block text-sm font-medium text-gray-700 mb-2">
                    Parceria com Guias
                  </label>
                  <textarea
                    id="est-desconto-parceria-texto"
                    value={form.descontoParceriaDescricao}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, descontoParceriaDescricao: e.target.value }));
                      setDirty(true);
                    }}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[160px] resize-y text-sm"
                    placeholder="Ex.: 10% de desconto em consumo para utilizadores com reserva Navi; parceria válida de segunda a quinta…"
                  />
                </div>
              </section>
            )}

          {categoryModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4" onClick={() => setCategoryModalOpen(false)}>
              <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Nova categoria</h3>
                    <p className="text-sm text-gray-600">Cadastre uma nova categoria para estabelecimentos.</p>
                  </div>
                </div>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  placeholder="Nome da categoria"
                />
                <div className="flex gap-3 justify-end mt-4">
                  <button type="button" onClick={() => setCategoryModalOpen(false)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                    Cancelar
                  </button>
                  <button type="button" onClick={() => void saveNewCategory()} className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600">
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          )}

          {subcategoryModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4" onClick={() => setSubcategoryModalOpen(false)}>
              <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Nova subcategoria</h3>
                    <p className="text-sm text-gray-600">Associe a subcategoria a uma categoria.</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <select
                    value={newSubcategoryCategoryId}
                    onChange={(e) => setNewSubcategoryCategoryId(e.target.value)}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  >
                    <option value="">Selecione a categoria</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newSubcategoryName}
                    onChange={(e) => setNewSubcategoryName(e.target.value)}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    placeholder="Nome da subcategoria"
                  />
                </div>
                <div className="flex gap-3 justify-end mt-4">
                  <button type="button" onClick={() => setSubcategoryModalOpen(false)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                    Cancelar
                  </button>
                  <button type="button" onClick={() => void saveNewSubcategory()} className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600">
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          )}

          {categoriasManagerOpen && !reassignModal && (
            <div className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4" onClick={() => setCategoriasManagerOpen(false)}>
              <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Gerenciar categorias</h3>
                    <p className="text-sm text-gray-600">Crie, exclua ou gerencie categorias e subcategorias.</p>
                  </div>
                </div>
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => { setCategoriasManagerOpen(false); setCategoryModalOpen(true); }}
                    className="px-3 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
                  >
                    Nova categoria
                  </button>
                  <button
                    type="button"
                    onClick={() => { setCategoriasManagerOpen(false); setSubcategoryModalOpen(true); }}
                    className="px-3 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
                  >
                    Nova subcategoria
                  </button>
                </div>
                <div className="space-y-4">
                  {categories.map((cat) => (
                    <div key={cat.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{cat.name}</span>
                        <button
                          type="button"
                          onClick={() => void handleDeleteCategory(cat)}
                          className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          Excluir
                        </button>
                      </div>
                      <div className="space-y-1 pl-2 border-l-2 border-gray-200">
                        {getEstabelecimentoSubcategoriesByCategory(cat.id).map((sub) => (
                          <div key={sub.id} className="flex items-center justify-between py-1">
                            <span className="text-sm text-gray-600">{sub.name}</span>
                            <button
                              type="button"
                              onClick={() => void handleDeleteSubcategory(sub)}
                              className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded"
                            >
                              Excluir
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-4">
                  <button type="button" onClick={() => setCategoriasManagerOpen(false)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}

          {reassignModal && (
            <div className="fixed inset-0 bg-black/50 z-[350] flex items-center justify-center p-4" onClick={() => setReassignModal(null)}>
              <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Reatribuir antes de excluir</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {`${reassignModal.establishmentsCount} estabelecimento(s) `}
                      em &quot;{reassignModal.name}&quot;. Escolha uma nova categoria e subcategoria para eles:
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nova categoria</label>
                    <select
                      value={reassignNewCategoryId}
                      onChange={(e) => {
                        setReassignNewCategoryId(e.target.value);
                        setReassignNewSubcategoryId('');
                      }}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    >
                      <option value="">Selecione</option>
                      {categories.filter((c) => reassignModal.type !== 'category' || c.id !== reassignModal.id).map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {reassignModal.type === 'category' && categories.filter((c) => c.id !== reassignModal.id).length === 0 && (
                      <p className="text-xs text-amber-700 mt-1">Cadastre outra categoria antes de excluir esta.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nova subcategoria</label>
                    <select
                      value={reassignNewSubcategoryId}
                      onChange={(e) => setReassignNewSubcategoryId(e.target.value)}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    >
                      <option value="">Selecione</option>
                      {reassignNewCategoryId &&
                        getEstabelecimentoSubcategoriesByCategory(reassignNewCategoryId)
                          .filter((s) => reassignModal.type !== 'subcategory' || s.id !== reassignModal.id)
                          .map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 justify-end mt-4">
                  <button type="button" onClick={() => setReassignModal(null)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => void confirmReassignAndDelete()}
                    disabled={!reassignNewCategoryId || !reassignNewSubcategoryId}
                    className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reatribuir e excluir
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal de edição do dia do calendário */}
          {diaEditModal && (
            <DiaEditModal
              dataStr={diaEditModal.dataStr}
              exc={diaEditModal.exc}
              estabelecimentoId={editingId ?? undefined}
              onSave={(exc) => {
                setExcecaoDia(diaEditModal.dataStr, exc);
                setDiaEditModal(null);
              }}
              onRemove={() => {
                setExcecaoDia(diaEditModal.dataStr, null);
                setDiaEditModal(null);
              }}
              onClose={() => setDiaEditModal(null)}
              onCriarEvento={(dataStr) => {
                setDiaEditModal(null);
                navigate('/eventos', { state: { startDate: dataStr, endDate: dataStr, estabelecimentoId: editingId ?? undefined } });
              }}
            />
          )}

          {rascunhoLeaveModalOpen && (
            <div
              className="fixed inset-0 z-[335] flex items-center justify-center p-4 bg-black/50"
              onClick={() => setRascunhoLeaveModalOpen(false)}
            >
              <div
                className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border border-gray-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex gap-4 mb-6">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Alterações não salvas</h3>
                    <p className="text-sm text-gray-600">
                      Deseja guardar o rascunho (incluindo o status programado) antes de voltar à lista? Se sair sem guardar, as
                      alterações ao status programado não serão atualizadas.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setRascunhoLeaveModalOpen(false);
                      void persistEstDraftRef.current({ keepOnForm: false });
                    }}
                    className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors order-1"
                  >
                    Salvar e sair
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRascunhoLeaveModalOpen(false);
                      setDirty(false);
                      setViewMode('list');
                      setEditingId(null);
                    }}
                    className="px-4 py-2.5 rounded-xl font-medium text-gray-800 bg-white border border-gray-300 hover:bg-gray-50 transition-colors order-2"
                  >
                    Sair
                  </button>
                  <button
                    type="button"
                    onClick={() => setRascunhoLeaveModalOpen(false)}
                    className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors order-3"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
